"use client";
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'fr' | 'en' | 'de' | 'it';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'fr',
  setLang: () => {},
  t: (key) => key,
});

export function useLanguage() {
  return useContext(LanguageContext);
}

const translations: Record<Language, Record<string, string>> = {
  fr: {
    'nav.home': 'Accueil',
    'nav.pricing': 'Offres & Tarifs',
    'nav.marketing': 'Marketing',
    'nav.ai': 'IA & Automatisation',
    'nav.contact': 'Contact',
    'nav.espace_client': 'Espace Client',
    'nav.start_project': 'Démarrer mon projet',
    'home.badge': '🇨🇭 Solutions Digitales Suisses',
    'home.hero_title': 'Votre présence digitale,',
    'home.hero_title_accent': 'clé en main',
    'home.hero_subtitle': 'Sites web professionnels, marketing digital et automatisation IA pour PME suisses. À partir de',
    'home.hero_subtitle_setup': '890.- setup',
    'home.hero_subtitle_and': 'et',
    'home.hero_subtitle_monthly': '19.90.-/mois',
    'home.cta_offers': 'Voir les offres',
    'home.cta_expert': 'Parler à un expert',
    'home.trust_no_fees': 'Pas de frais cachés',
    'home.trust_support': 'Support en français',
    'home.trust_cancel': 'Résiliation mensuelle',
    'home.scroll_discover': 'Découvrir',
    'home.stats_sites': 'Sites livrés',
    'home.stats_clients': 'Clients satisfaits',
    'home.stats_delay': 'Délai de réponse',
    'home.stats_packs': 'Packs disponibles',
    'home.problem_badge': 'Le problème',
    'home.problem_title': "Votre business mérite mieux qu'un site",
    'home.problem_title_strike': 'amateur',
    'home.problem_sub': "En Suisse, 67% des PME perdent des clients faute d'une présence digitale professionnelle. Pendant ce temps, vos concurrents captent ces clients en ligne.",
    'home.problem_1': 'Site non trouvable sur Google',
    'home.problem_2': 'Design non adapté mobile',
    'home.problem_3': 'Aucun système de capture de leads',
    'home.problem_4': 'Pas de marketing automatisé',
    'home.solution_badge': 'Notre solution',
    'home.solution_title': 'Une solution complète,',
    'home.solution_title_accent': 'sans complexité',
    'home.solution_sub': "ECR360 Digital prend en charge votre présence digitale de A à Z. Vous vous concentrez sur votre métier, nous gérons le reste.",
    'home.solution_1': 'Site professionnel livré en 5 jours',
    'home.solution_2': 'Référencement Google optimisé dès le départ',
    'home.solution_3': 'Automatisation marketing intégrée',
    'home.solution_4': 'Support en français, équipe suisse',
    'home.packs_title': 'Nos',
    'home.packs_title_accent': 'Packs',
    'home.packs_section_badge': 'Nos Packs',
    'home.packs_section_title': '3 packs pour',
    'home.packs_section_accent': 'chaque ambition',
    'home.packs_section_sub': 'Choisissez le pack adapté à votre situation. Évoluez à tout moment.',
    'home.packs_compare': 'Voir la comparaison détaillée →',
    'home.pack1_title': 'Site Sans Entretien',
    'home.pack2_title': 'Site + Entretien',
    'home.pack3_title': 'Site + Marketing',
    'home.pack_setup': 'Setup unique',
    'home.pack_month': '/mois',
    'home.pack_choose': 'Choisir',
    'home.pack_popular': 'Le plus populaire',
    'home.pack1_f1': 'Site web professionnel',
    'home.pack1_f2': 'Design responsive mobile',
    'home.pack1_f3': 'Hébergement inclus',
    'home.pack1_f4': 'Nom de domaine (.ch)',
    'home.pack1_f5': 'Certificat SSL',
    'home.pack1_f6': 'Formulaire de contact',
    'home.pack2_f1': 'Tout du Pack 1',
    'home.pack2_f2': 'Mises à jour mensuelles',
    'home.pack2_f3': 'Maintenance technique',
    'home.pack2_f4': 'Sauvegardes automatiques',
    'home.pack2_f5': 'Support prioritaire',
    'home.pack2_f6': 'Rapport mensuel',
    'home.pack3_f1': 'Tout du Pack 2',
    'home.pack3_f2': 'Stratégie SEO locale',
    'home.pack3_f3': 'Gestion réseaux sociaux',
    'home.pack3_f4': 'Campagnes Google Ads',
    'home.pack3_f5': 'Email marketing',
    'home.pack3_f6': 'Tableau de bord analytics',
    'home.services_badge': 'Nos Services',
    'home.services_title': 'Plus que des sites web —',
    'home.services_title_accent': 'un écosystème digital',
    'home.services_marketing_title': 'Solutions Marketing Digital',
    'home.services_marketing_desc': "SEO local, Google Ads, réseaux sociaux, email marketing. Tout ce dont vous avez besoin pour attirer et convertir des clients en ligne.",
    'home.services_marketing_discover': 'Découvrir →',
    'home.services_ai_title': 'Automatisation IA',
    'home.services_ai_desc': "Chatbots, automatisation des emails, qualification de leads. L'IA au service de votre croissance.",
    'home.services_ai_explore': "Explorer l'IA →",
    'home.services_support_title': 'Support Suisse',
    'home.services_support_desc': 'Équipe basée en Suisse, support en français, réponse sous 48h garantie.',
    'home.services_speed_title': 'Livraison en 5 jours',
    'home.services_speed_desc': "Votre site professionnel mis en ligne en moins d'une semaine.",
    'home.services_analytics_title': 'Analytics inclus',
    'home.services_analytics_desc': 'Tableau de bord clair pour suivre vos performances en temps réel.',
    'home.testimonials_title': 'Ce que disent',
    'home.testimonials_title_accent': 'nos clients',
    'home.testimonials_badge': 'Témoignages',
    'home.t1_quote': "ECR360 Digital a transformé notre présence en ligne. En 3 mois, nos demandes de devis ont augmenté de 60%. Service impeccable, équipe réactive.",
    'home.t1_role': 'Gérant, Plomberie Dubois SA',
    'home.t2_quote': "Le Pack 3 nous a permis d'automatiser notre marketing. On reçoit maintenant des leads qualifiés chaque semaine sans effort de notre part.",
    'home.t2_role': 'Directrice, Cabinet Favre RH',
    'home.t3_quote': 'Rapport qualité-prix imbattable pour la Suisse. Notre site est magnifique et le support est toujours disponible. Je recommande vivement.',
    'home.t3_role': 'Fondateur, Müller Consulting',
    'home.cta_section_title': 'Prêt à lancer votre',
    'home.cta_section_accent': 'projet digital ?',
    'home.cta_section_sub': 'Rejoignez plus de 150 PME suisses qui nous font confiance.',
    'home.cta_section_btn': 'Démarrer mon projet',
    'home.cta_section_btn2': 'Voir les offres',
    'home.cta_section_note': "Consultation 100% gratuite · Réponse sous 48h · Pas d'engagement",
    'pricing.title': 'Nos',
    'pricing.title_accent': 'Offres & Tarifs',
    'pricing.hero_title': 'Choisissez votre',
    'pricing.hero_title_accent': 'Pack Digital',
    'pricing.subtitle': "Des solutions adaptées à chaque budget. Setup unique de 890.- puis abonnement mensuel sans engagement.",
    'pricing.setup': 'Setup unique',
    'pricing.month': '/mois',
    'pricing.choose': 'Choisir',
    'pricing.popular': 'Le plus populaire',
    'pricing.faq_title': 'Questions fréquentes',
    'pricing.faq_badge': 'FAQ',
    'pricing.start_btn': 'Démarrer mon projet',
    'pricing.contact_btn': 'Contacter notre équipe',
    'pricing.custom_need': "Besoin d'une solution sur mesure?",
    'pricing.custom_link': 'Contactez-nous pour un devis personnalisé →',
    'pricing.setup_title': 'À propos du setup de CHF 890.-',
    'pricing.setup_desc': "Le frais de setup est un investissement unique qui couvre la création complète de votre site web: design sur mesure, développement, configuration technique, mise en ligne, et formation initiale.",
    'pricing.setup_f1': 'Design personnalisé à votre image',
    'pricing.setup_f2': 'Développement & intégration',
    'pricing.setup_f3': "Configuration hébergement & domaine",
    'pricing.setup_f4': "Formation à l'utilisation",
    'pricing.setup_f5': 'Tests & optimisation performance',
    'pricing.setup_f6': 'Mise en ligne & vérification',
    'pricing.cta_title': 'Prêt à démarrer?',
    'pricing.cta_sub': 'Consultation gratuite pour choisir le pack adapté à votre situation.',
    'pricing.pack1_title': 'Site Sans Entretien',
    'pricing.pack1_subtitle': 'Parfait pour démarrer',
    'pricing.pack1_ideal': 'Idéal pour les indépendants et petites structures qui veulent une vitrine professionnelle.',
    'pricing.pack1_cta': 'Choisir Pack 1',
    'pricing.pack1_f1': "Site web professionnel (jusqu'à 5 pages)",
    'pricing.pack1_f2': 'Design responsive mobile & tablette',
    'pricing.pack1_f3': 'Hébergement suisse inclus',
    'pricing.pack1_f4': 'Nom de domaine .ch ou .com',
    'pricing.pack1_f5': 'Certificat SSL (HTTPS)',
    'pricing.pack1_f6': 'Formulaire de contact',
    'pricing.pack1_f7': 'Intégration Google Maps',
    'pricing.pack1_f8': 'Mises à jour du contenu',
    'pricing.pack1_f9': 'Maintenance technique',
    'pricing.pack1_f10': 'SEO optimisé',
    'pricing.pack1_f11': 'Support prioritaire',
    'pricing.pack1_f12': 'Solutions Marketing',
    'pricing.pack2_title': 'Site + Entretien',
    'pricing.pack2_subtitle': 'Le plus populaire',
    'pricing.pack2_ideal': 'Idéal pour les PME qui veulent une présence digitale maintenue et optimisée en continu.',
    'pricing.pack2_cta': 'Choisir Pack 2',
    'pricing.pack2_f1': "Site web professionnel (jusqu'à 10 pages)",
    'pricing.pack2_f2': 'Design responsive mobile & tablette',
    'pricing.pack2_f3': 'Hébergement suisse inclus',
    'pricing.pack2_f4': 'Nom de domaine .ch ou .com',
    'pricing.pack2_f5': 'Certificat SSL (HTTPS)',
    'pricing.pack2_f6': 'Formulaire de contact avancé',
    'pricing.pack2_f7': 'Intégration Google Maps',
    'pricing.pack2_f8': 'Mises à jour du contenu (2x/mois)',
    'pricing.pack2_f9': 'Maintenance technique',
    'pricing.pack2_f10': 'SEO de base',
    'pricing.pack2_f11': 'Support prioritaire (48h)',
    'pricing.pack2_f12': 'Solutions Marketing',
    'pricing.pack3_title': 'Site + Marketing',
    'pricing.pack3_subtitle': 'Solution complète',
    'pricing.pack3_ideal': 'Idéal pour les entreprises ambitieuses qui veulent générer des leads et croître rapidement.',
    'pricing.pack3_cta': 'Choisir Pack 3',
    'pricing.pack3_f1': 'Site web professionnel (pages illimitées)',
    'pricing.pack3_f2': 'Design responsive mobile & tablette',
    'pricing.pack3_f3': 'Hébergement suisse inclus',
    'pricing.pack3_f4': 'Nom de domaine .ch ou .com',
    'pricing.pack3_f5': 'Certificat SSL (HTTPS)',
    'pricing.pack3_f6': 'Formulaires avancés + CRM',
    'pricing.pack3_f7': 'Intégration Google Maps',
    'pricing.pack3_f8': 'Mises à jour illimitées',
    'pricing.pack3_f9': 'Maintenance technique complète',
    'pricing.pack3_f10': 'SEO avancé + rapport mensuel',
    'pricing.pack3_f11': 'Support dédié (24h)',
    'pricing.pack3_f12': 'Google Ads + Social Media + Email',
    'pricing.faq1_q': 'Puis-je changer de pack après avoir souscrit?',
    'pricing.faq1_a': "Oui, vous pouvez upgrader ou downgrader votre pack à tout moment. La modification prend effet au prochain cycle de facturation.",
    'pricing.faq2_q': 'Le setup de 890.- est-il obligatoire?',
    'pricing.faq2_a': "Oui, le setup unique de CHF 890.- couvre la création, le design et la mise en ligne de votre site. Il n'est payé qu'une seule fois.",
    'pricing.faq3_q': 'Puis-je résilier à tout moment?',
    'pricing.faq3_a': "Oui, nos abonnements sont sans engagement longue durée. Vous pouvez résilier avec un préavis d'un mois.",
    'pricing.faq4_q': 'Que se passe-t-il avec mon site si je résilie?',
    'pricing.faq4_a': "Vous gardez votre nom de domaine et nous vous fournissons une export de votre site. Vous restez propriétaire de votre contenu.",
    'pricing.faq5_q': 'Les prix sont-ils en CHF?',
    'pricing.faq5_a': 'Oui, tous nos prix sont en Francs Suisses (CHF), TVA incluse.',
    'pricing.faq6_q': 'Proposez-vous des tarifs pour les associations?',
    'pricing.faq6_a': 'Oui, nous proposons des tarifs préférentiels pour les associations et organisations à but non lucratif. Contactez-nous pour un devis personnalisé.',
    'marketing.badge': 'Solutions Marketing Digital',
    'marketing.title': 'Boostez votre',
    'marketing.title_accent': 'visibilité en ligne',
    'marketing.subtitle': 'Des stratégies marketing digitales complètes pour attirer plus de clients et développer votre activité.',
    'marketing.cta': 'Démarrer mon projet marketing',
    'marketing.cta_audit': 'Audit gratuit de ma présence',
    'marketing.partner_title': 'Partenaire certifié Google',
    'marketing.partner_sub': "Nous maîtrisons l'ensemble de l'écosystème Google pour votre croissance",
    'marketing.services_badge': 'Nos services',
    'marketing.services_title': 'Tout ce dont vous avez besoin',
    'marketing.services_sub': "De la visibilité locale à la publicité payante, en passant par les réseaux sociaux et l'email marketing.",
    'marketing.process_badge': 'Notre processus',
    'marketing.process_title': 'Comment ça fonctionne',
    'marketing.process_1_title': 'Audit Digital',
    'marketing.process_1_desc': 'Analyse complète de votre présence digitale actuelle et de celle de vos concurrents.',
    'marketing.process_2_title': 'Stratégie Sur Mesure',
    'marketing.process_2_desc': "Définition d'une stratégie marketing adaptée à votre secteur, vos objectifs et votre budget.",
    'marketing.process_3_title': 'Mise en Oeuvre',
    'marketing.process_3_desc': 'Déploiement des actions marketing avec suivi hebdomadaire et optimisations continues.',
    'marketing.process_4_title': 'Mesure & Optimisation',
    'marketing.process_4_desc': 'Reporting mensuel avec KPIs clés et ajustements stratégiques pour maximiser le ROI.',
    'marketing.results_badge': 'Résultats concrets',
    'marketing.results_title': "Des chiffres qui parlent d'eux-mêmes",
    'marketing.results_sub': 'Nos clients PME suisses voient des résultats mesurables dès les premiers mois. Performances moyennes sur 6 mois.',
    'marketing.cta_section_title': 'Prêt à booster votre marketing?',
    'marketing.cta_section_sub': 'Audit digital gratuit de votre présence actuelle. Sans engagement.',
    'marketing.cta_audit_free': 'Demander mon audit gratuit',
    'marketing.cta_submit': 'Soumettre mon projet',
    'ai.badge': 'IA & Automatisation',
    'ai.title': 'Automatisez votre',
    'ai.title_accent': 'croissance',
    'ai.subtitle': "L'intelligence artificielle au service de votre entreprise. Gagnez du temps, réduisez les coûts et augmentez vos conversions.",
    'ai.cta': 'Découvrir nos solutions IA',
    'ai.cta_demo': 'Demander une démo IA',
    'ai.cta_pricing': 'Voir les tarifs',
    'ai.chatbot_label': 'Chatbot ECR360 — En direct sur votre site',
    'ai.chatbot_active': 'Actif 24/7',
    'ai.solutions_badge': 'Solutions IA',
    'ai.solutions_title': "Tout ce que l'IA peut faire pour vous",
    'ai.usecases_badge': "Cas d'usage",
    'ai.usecases_title': "L'IA dans votre secteur",
    'ai.roi_badge': 'ROI Estimé',
    'ai.roi_title': "Combien l'IA peut vous faire économiser?",
    'ai.roi_sub': 'En moyenne, nos clients PME économisent',
    'ai.roi_highlight': '23h par semaine',
    'ai.roi_sub2': "grâce à l'automatisation IA.",
    'ai.roi_stat1_label': 'Économisées/semaine',
    'ai.roi_stat1_sub': 'En tâches répétitives',
    'ai.roi_stat2_label': 'Taux de résolution',
    'ai.roi_stat2_sub': 'Chatbot IA',
    'ai.roi_stat3_label': 'Plus de conversions',
    'ai.roi_stat3_sub': 'Sur leads automatisés',
    'ai.cta_section_title': 'Prêt à automatiser votre business?',
    'ai.cta_section_sub': "Démo gratuite de 30 minutes pour voir l'IA en action dans votre secteur.",
    'ai.cta_demo_free': 'Demander ma démo gratuite',
    'ai.sol1_title': 'Chatbot Intelligent',
    'ai.sol1_desc': "Répondez automatiquement aux questions fréquentes de vos visiteurs 24h/24. Qualifiez les leads avant qu'ils vous contactent.",
    'ai.sol1_benefit': '15h/semaine de support client économisées',
    'ai.sol1_tag': 'IA Conversationnelle',
    'ai.sol2_title': 'Email Automation',
    'ai.sol2_desc': "Séquences d'emails automatiques déclenchées par les actions de vos prospects. Nurturing intelligent jusqu'à la conversion.",
    'ai.sol2_benefit': '4x plus de conversions sur les leads froids',
    'ai.sol2_tag': 'Marketing Automation',
    'ai.sol3_title': 'Lead Scoring IA',
    'ai.sol3_desc': "L'IA analyse le comportement de vos visiteurs et score chaque lead selon sa probabilité de conversion. Concentrez-vous sur les meilleurs prospects.",
    'ai.sol3_benefit': '60% de réduction du temps de qualification',
    'ai.sol3_tag': 'Intelligence Artificielle',
    'ai.sol4_title': 'Reporting Automatisé',
    'ai.sol4_desc': 'Rapports de performance générés automatiquement chaque semaine. Insights actionnables sans effort de votre part.',
    'ai.sol4_benefit': '3x plus rapides basées sur les données',
    'ai.sol4_tag': 'Business Intelligence',
    'ai.sol5_title': 'Intégrations & CRM',
    'ai.sol5_desc': 'Connectez votre site à votre CRM, agenda, et outils métier. Tout synchronisé automatiquement, zéro saisie manuelle.',
    'ai.sol5_benefit': '8h/semaine gagnées sur les tâches administratives',
    'ai.sol5_tag': 'Automatisation',
    'ai.sol6_title': 'Génération de Contenu IA',
    'ai.sol6_desc': "L'IA génère des variantes de textes pour vos publicités, emails et posts sociaux. Testez plus vite, optimisez en continu.",
    'ai.sol6_benefit': '10x plus de variations testées par mois',
    'ai.sol6_tag': 'IA Générative',
    'ai.uc1_sector': 'Artisans & PME',
    'ai.uc1_scenario': "Un plombier reçoit automatiquement les demandes de devis via chatbot, qualifiées par l'IA, avec notification SMS instantanée.",
    'ai.uc1_saving': '12h/semaine économisées',
    'ai.uc2_sector': 'Professionnels libéraux',
    'ai.uc2_scenario': 'Un cabinet médical automatise les rappels de rendez-vous, les suivis patients et les newsletters mensuelles.',
    'ai.uc2_saving': '8h/semaine économisées',
    'ai.uc3_sector': 'Commerce & Retail',
    'ai.uc3_scenario': "Une boutique envoie des emails de relance automatiques après abandon de panier, avec offres personnalisées par l'IA.",
    'ai.uc3_saving': '+35% de récupération de paniers',
    'contact.badge': 'Contactez-nous',
    'contact.title': 'Parlons de votre',
    'contact.title_accent': 'projet',
    'contact.subtitle': 'Notre équipe est disponible pour répondre à toutes vos questions et vous accompagner dans votre transformation digitale.',
    'contact.firstname': 'Prénom',
    'contact.lastname': 'Nom',
    'contact.email': 'Email',
    'contact.company': 'Entreprise',
    'contact.phone': 'Téléphone',
    'contact.pack': 'Pack souhaité',
    'contact.message': 'Message',
    'contact.send': 'Envoyer le message',
    'contact.sending': 'Envoi en cours...',
    'contact.success_title': 'Message envoyé !',
    'contact.success_sub': 'Merci pour votre message. Notre équipe vous répondra dans les 48 heures.',
    'contact.back_home': "Retour à l'accueil",
    'contact.form_title': 'Envoyez-nous votre demande',
    'contact.form_sub': 'Tous les champs marqués * sont obligatoires.',
    'contact.message_placeholder': 'Parlez-nous de votre entreprise, vos objectifs digitaux et ce que vous recherchez...',
    'contact.privacy': "En soumettant ce formulaire, vous acceptez que vos données soient utilisées pour vous contacter concernant votre projet. Conformément à la LPD suisse, vous pouvez demander la suppression de vos données à tout moment.",
    'contact.coords_title': 'Nos coordonnées',
    'contact.next_title': 'Ce qui se passe ensuite',
    'contact.next_1': 'Nous analysons votre demande et votre secteur',
    'contact.next_2': 'Appel de 30 min pour comprendre vos objectifs',
    'contact.next_3': 'Proposition personnalisée avec devis transparent',
    'contact.next_4': 'Démarrage du projet sous 5 jours ouvrables',
    'contact.why_title': 'Pourquoi ECR360 Digital?',
    'contact.why_1': '150+ sites livrés en Suisse',
    'contact.why_2': 'Équipe 100% suisse, support en français',
    'contact.why_3': 'Tarifs transparents, sans surprise',
    'contact.why_4': 'Résiliation mensuelle sans pénalité',
    'contact.why_5': 'Consultation initiale gratuite',
    'contact.packs_title': 'Rappel de nos offres',
    'contact.packs_link': 'Voir la comparaison complète des packs →',
    'contact.pack_opt_default': 'Pack souhaité',
    'contact.pack_opt_1': 'Pack 1 — Site Sans Entretien (19.90.-/mois)',
    'contact.pack_opt_2': 'Pack 2 — Site + Entretien (39.90.-/mois)',
    'contact.pack_opt_3': 'Pack 3 — Site + Marketing (99.90.-/mois)',
    'contact.pack_opt_marketing': 'Solutions Marketing uniquement',
    'contact.pack_opt_ai': 'Automatisation IA uniquement',
    'contact.pack_opt_custom': 'Solution sur mesure',
    'contact.info_email': 'Email',
    'contact.info_website': 'Site web',
    'contact.info_location': 'Localisation',
    'contact.info_response': 'Réponse',
    'contact.info_response_val': 'Sous 48 heures',
    'contact.pack_reminder_chf': 'CHF/mois',
    'demande.badge': 'Demande de Projet',
    'demande.title': 'Démarrez votre',
    'demande.title_accent': 'projet digital',
    'demande.subtitle': "Remplissez ce formulaire et notre équipe vous contactera sous 48h pour discuter de votre projet et vous proposer la meilleure solution.",
    'demande.step1': 'Vos informations',
    'demande.step2': 'Type de site',
    'demande.step3': 'Pack choisi',
    'demande.step4': 'Services additionnels',
    'demande.step4_sub': "En plus de votre site web, avez-vous besoin d'autres services ? Cochez tout ce qui vous intéresse.",
    'demande.step5': 'Description du projet',
    'demande.step6': 'Logo',
    'demande.step7': 'Palette de couleurs',
    'demande.step7_sub': "Choisissez une palette de couleurs qui correspond à l'image que vous souhaitez donner à votre site.",
    'demande.step8': 'Informations complémentaires',
    'demande.optional': '(optionnel)',
    'demande.required_fields': '* Champs obligatoires. Vos données sont traitées de manière confidentielle.',
    'demande.submit': 'Envoyer ma demande',
    'demande.submitting': 'Envoi en cours...',
    'demande.firstname': 'Prénom',
    'demande.lastname': 'Nom',
    'demande.email': 'Email',
    'demande.phone': 'Téléphone',
    'demande.company': "Nom de l'entreprise",
    'demande.url': 'URL souhaitée',
    'demande.description_placeholder': "Décrivez votre projet, vos objectifs, votre secteur d'activité...",
    'demande.deadline': 'Deadline souhaitée',
    'demande.extra_message': 'Message supplémentaire',
    'demande.extra_message_placeholder': 'Toute information supplémentaire que vous souhaitez nous communiquer...',
    'demande.logo_drop': 'Déposez votre logo ici',
    'demande.logo_formats': 'PNG, JPG ou SVG — max 5MB',
    'demande.logo_delete': 'Supprimer',
    'demande.logo_create': "Je n'ai pas de logo — j'aimerais en faire créer un",
    'demande.logo_create_sub': "Notre équipe vous proposera des options de création de logo adaptées à votre identité.",
    'demande.palette_selected': 'Palette sélectionnée :',
    'demande.palette_clear': 'Effacer',
    'demande.marketing_title': 'Solutions Marketing Digital',
    'demande.marketing_sub': 'Google, réseaux sociaux, publicités en ligne',
    'demande.ai_title': 'Automatisation & Intelligence Artificielle',
    'demande.ai_sub': 'Chatbots, workflows automatiques, IA sur mesure',
    'demande.email_pro_title': 'Adresses email professionnelles',
    'demande.email_pro_sub': 'Ex: contact@votre-entreprise.ch — Emails professionnels hébergés en Suisse avec votre nom de domaine.',
    'demande.success_title': 'Demande envoyée !',
    'demande.success_sub': 'Votre demande de projet a bien été reçue. Notre équipe vous contactera dans les',
    'demande.success_sub2': '48 heures',
    'demande.success_sub3': 'pour discuter de votre projet.',
    'demande.pack_label': 'Pack choisi',
    'demande.site_type_label': 'Type de site',
    'demande.marketing_count': 'service(s)',
    'demande.ai_count': 'service(s)',
    'demande.back_home': "Retour à l'accueil",
    'demande.see_offers': 'Voir nos offres',
    'demande.type_vitrine': 'Site Vitrine',
    'demande.type_vitrine_desc': 'Présentation de votre entreprise',
    'demande.type_ecommerce': 'E-Commerce',
    'demande.type_ecommerce_desc': 'Boutique en ligne avec paiements',
    'demande.type_portfolio': 'Portfolio',
    'demande.type_portfolio_desc': 'Showcase de vos réalisations',
    'demande.type_autre': 'Autre',
    'demande.type_autre_desc': 'Autre type de projet',
    'demande.pack_essentiel': 'Pack Essentiel',
    'demande.pack_essentiel_price': '890.- + 19.90.-/mois',
    'demande.pack_essentiel_desc': 'Site professionnel sans entretien',
    'demande.pack_confort': 'Pack Confort',
    'demande.pack_confort_price': '890.- + 39.90.-/mois',
    'demande.pack_confort_desc': 'Site + maintenance mensuelle',
    'demande.pack_croissance': 'Pack Croissance',
    'demande.pack_croissance_price': '890.- + 99.90.-/mois',
    'demande.pack_croissance_desc': 'Site + marketing complet',
    'demande.mkt_google_ads': 'Google Ads',
    'demande.mkt_google_ads_desc': 'Campagnes publicitaires payantes sur Google',
    'demande.mkt_seo': 'SEO / Référencement',
    'demande.mkt_seo_desc': "Optimisation pour apparaître en 1ère page Google",
    'demande.mkt_gmb': 'Google My Business',
    'demande.mkt_gmb_desc': 'Fiche Google Maps et avis clients',
    'demande.mkt_social': 'Réseaux Sociaux',
    'demande.mkt_social_desc': 'Gestion Instagram, Facebook, LinkedIn',
    'demande.mkt_meta': 'Meta / LinkedIn Ads',
    'demande.mkt_meta_desc': 'Publicités sur Facebook, Instagram ou LinkedIn',
    'demande.mkt_email': 'Email Marketing',
    'demande.mkt_email_desc': 'Newsletters et campagnes email automatisées',
    'demande.ai_chatbot': 'Chatbot IA',
    'demande.ai_chatbot_desc': 'Assistant virtuel intelligent sur votre site',
    'demande.ai_auto': 'Automatisation des tâches',
    'demande.ai_auto_desc': 'Workflows automatiques (devis, emails, CRM)',
    'demande.ai_data': 'Analyse de données IA',
    'demande.ai_data_desc': 'Tableaux de bord et rapports intelligents',
    'demande.ai_content': 'Génération de contenu IA',
    'demande.ai_content_desc': 'Textes, descriptions produits, articles de blog',
    'demande.palette_bleu': 'Bleu Professionnel',
    'demande.palette_bleu_desc': 'Confiance, sérieux, corporate',
    'demande.palette_vert': 'Vert Nature',
    'demande.palette_vert_desc': 'Écologie, santé, bien-être',
    'demande.palette_rouge': 'Rouge Énergie',
    'demande.palette_rouge_desc': 'Dynamisme, passion, urgence',
    'demande.palette_violet': 'Violet Créatif',
    'demande.palette_violet_desc': 'Créativité, luxe, innovation',
    'demande.palette_orange': 'Orange Chaleureux',
    'demande.palette_orange_desc': 'Convivialité, énergie, artisanat',
    'demande.palette_gris': 'Gris Moderne',
    'demande.palette_gris_desc': 'Élégance, minimalisme, tech',
    'demande.palette_rose': 'Rose Élégant',
    'demande.palette_rose_desc': 'Beauté, mode, lifestyle',
    'demande.palette_jaune': 'Jaune Optimiste',
    'demande.palette_jaune_desc': 'Jeunesse, créativité, soleil',
    'demande.palette_turquoise': 'Turquoise Frais',
    'demande.palette_turquoise_desc': 'Modernité, fraîcheur, digital',
    'demande.palette_noir': 'Noir Luxe',
    'demande.palette_noir_desc': 'Prestige, haut de gamme, exclusivité',
    'demande.palette_multi': 'Multicolore / Vif',
    'demande.palette_multi_desc': 'Créatif, festif, diversité',
    'demande.palette_none': 'Pas de préférence',
    'demande.palette_none_desc': 'Laissez notre équipe choisir pour vous',
    'confirmation.title': 'Demande envoyée',
    'confirmation.title_accent': 'avec succès !',
    'confirmation.thanks': 'Merci',
    'confirmation.thanks_sub': ', votre demande a bien été reçue.',
    'confirmation.summary': 'Récapitulatif de votre demande',
    'confirmation.next_steps': 'Prochaines étapes',
    'confirmation.back_home': "Retour à l'accueil",
    'confirmation.new_request': 'Nouvelle demande',
    'confirmation.full_name': 'Nom complet',
    'confirmation.email': 'Email',
    'confirmation.company': 'Entreprise',
    'confirmation.pack': 'Pack choisi',
    'confirmation.services': 'Services sélectionnés',
    'confirmation.see_offers': 'Voir nos offres',
    'login.title': 'Connexion',
    'login.subtitle': 'Accédez à votre espace client',
    'login.email': 'Email',
    'login.password': 'Mot de passe',
    'login.forgot': 'Mot de passe oublié ?',
    'login.submit': 'Se connecter',
    'login.loading': 'Connexion...',
    'login.no_account': 'Pas encore de compte ?',
    'login.signup': 'Créer un compte',
    'signup.title': 'Créer un compte',
    'signup.subtitle': "Rejoignez l'espace client ECR360",
    'signup.company': "Nom de l'entreprise",
    'signup.email': 'Email',
    'signup.password': 'Mot de passe',
    'signup.confirm_password': 'Confirmer le mot de passe',
    'signup.submit': 'Créer mon compte',
    'signup.loading': 'Création...',
    'signup.already': 'Déjà un compte ?',
    'signup.login': 'Se connecter',
    'reset.title': 'Mot de passe oublié',
    'reset.subtitle': 'Entrez votre email pour recevoir un lien de réinitialisation.',
    'reset.email': 'Email',
    'reset.submit': 'Envoyer le lien',
    'reset.loading': 'Envoi...',
    'reset.success': 'Email envoyé ! Vérifiez votre boîte mail.',
    'reset.back_login': 'Retour à la connexion',
    'client.dashboard': 'Tableau de bord',
    'client.subscription': 'Abonnement',
    'client.invoices': 'Factures',
    'client.profile': 'Profil',
    'client.logout': 'Déconnexion',
    'client.welcome': 'Bienvenue',
  },

  en: {
    'nav.home': 'Home',
    'nav.pricing': 'Pricing',
    'nav.marketing': 'Marketing',
    'nav.ai': 'AI & Automation',
    'nav.contact': 'Contact',
    'nav.espace_client': 'Client Area',
    'nav.start_project': 'Start my project',
    'home.badge': '🇨🇭 Swiss Digital Solutions',
    'home.hero_title': 'Your digital presence,',
    'home.hero_title_accent': 'turnkey',
    'home.hero_subtitle': 'Professional websites, digital marketing and AI automation for Swiss SMEs. Starting from',
    'home.hero_subtitle_setup': '890.- setup',
    'home.hero_subtitle_and': 'and',
    'home.hero_subtitle_monthly': '19.90.-/month',
    'home.cta_offers': 'View offers',
    'home.cta_expert': 'Talk to an expert',
    'home.trust_no_fees': 'No hidden fees',
    'home.trust_support': 'French-speaking support',
    'home.trust_cancel': 'Monthly cancellation',
    'home.scroll_discover': 'Discover',
    'home.stats_sites': 'Sites delivered',
    'home.stats_clients': 'Satisfied clients',
    'home.stats_delay': 'Response time',
    'home.stats_packs': 'Available packs',
    'home.problem_badge': 'The problem',
    'home.problem_title': 'Your business deserves better than an',
    'home.problem_title_strike': 'amateur',
    'home.problem_sub': 'In Switzerland, 67% of SMEs lose customers due to a lack of professional digital presence. Meanwhile, your competitors are capturing those customers online.',
    'home.problem_1': 'Site not found on Google',
    'home.problem_2': 'Design not adapted for mobile',
    'home.problem_3': 'No lead capture system',
    'home.problem_4': 'No automated marketing',
    'home.solution_badge': 'Our solution',
    'home.solution_title': 'A complete solution,',
    'home.solution_title_accent': 'without complexity',
    'home.solution_sub': 'ECR360 Digital handles your digital presence from A to Z. You focus on your business, we handle the rest.',
    'home.solution_1': 'Professional site delivered in 5 days',
    'home.solution_2': 'Google SEO optimized from the start',
    'home.solution_3': 'Integrated marketing automation',
    'home.solution_4': 'French-speaking support, Swiss team',
    'home.packs_title': 'Our',
    'home.packs_title_accent': 'Packs',
    'home.packs_section_badge': 'Our Packs',
    'home.packs_section_title': '3 packs for',
    'home.packs_section_accent': 'every ambition',
    'home.packs_section_sub': 'Choose the pack suited to your situation. Upgrade at any time.',
    'home.packs_compare': 'View detailed comparison →',
    'home.pack1_title': 'Site Without Maintenance',
    'home.pack2_title': 'Site + Maintenance',
    'home.pack3_title': 'Site + Marketing',
    'home.pack_setup': 'One-time setup',
    'home.pack_month': '/month',
    'home.pack_choose': 'Choose',
    'home.pack_popular': 'Most popular',
    'home.pack1_f1': 'Professional website',
    'home.pack1_f2': 'Mobile responsive design',
    'home.pack1_f3': 'Hosting included',
    'home.pack1_f4': 'Domain name (.ch)',
    'home.pack1_f5': 'SSL certificate',
    'home.pack1_f6': 'Contact form',
    'home.pack2_f1': 'Everything in Pack 1',
    'home.pack2_f2': 'Monthly updates',
    'home.pack2_f3': 'Technical maintenance',
    'home.pack2_f4': 'Automatic backups',
    'home.pack2_f5': 'Priority support',
    'home.pack2_f6': 'Monthly report',
    'home.pack3_f1': 'Everything in Pack 2',
    'home.pack3_f2': 'Local SEO strategy',
    'home.pack3_f3': 'Social media management',
    'home.pack3_f4': 'Google Ads campaigns',
    'home.pack3_f5': 'Email marketing',
    'home.pack3_f6': 'Analytics dashboard',
    'home.services_badge': 'Our Services',
    'home.services_title': 'More than websites —',
    'home.services_title_accent': 'a digital ecosystem',
    'home.services_marketing_title': 'Digital Marketing Solutions',
    'home.services_marketing_desc': 'Local SEO, Google Ads, social media, email marketing. Everything you need to attract and convert customers online.',
    'home.services_marketing_discover': 'Discover →',
    'home.services_ai_title': 'AI Automation',
    'home.services_ai_desc': 'Chatbots, email automation, lead qualification. AI at the service of your growth.',
    'home.services_ai_explore': 'Explore AI →',
    'home.services_support_title': 'Swiss Support',
    'home.services_support_desc': 'Team based in Switzerland, French-speaking support, 48h response guaranteed.',
    'home.services_speed_title': 'Delivered in 5 days',
    'home.services_speed_desc': 'Your professional site live in less than a week.',
    'home.services_analytics_title': 'Analytics included',
    'home.services_analytics_desc': 'Clear dashboard to track your performance in real time.',
    'home.testimonials_title': 'What our',
    'home.testimonials_title_accent': 'clients say',
    'home.testimonials_badge': 'Testimonials',
    'home.t1_quote': 'ECR360 Digital transformed our online presence. In 3 months, our quote requests increased by 60%. Impeccable service, responsive team.',
    'home.t1_role': 'Manager, Dubois Plumbing SA',
    'home.t2_quote': 'Pack 3 allowed us to automate our marketing. We now receive qualified leads every week without any effort on our part.',
    'home.t2_role': 'Director, Favre HR Firm',
    'home.t3_quote': 'Unbeatable value for money in Switzerland. Our site is beautiful and support is always available. I highly recommend.',
    'home.t3_role': 'Founder, Müller Consulting',
    'home.cta_section_title': 'Ready to launch your',
    'home.cta_section_accent': 'digital project?',
    'home.cta_section_sub': 'Join over 150 Swiss SMEs who trust us.',
    'home.cta_section_btn': 'Start my project',
    'home.cta_section_btn2': 'View offers',
    'home.cta_section_note': '100% free consultation · Response within 48h · No commitment',
    'pricing.title': 'Our',
    'pricing.title_accent': 'Pricing',
    'pricing.hero_title': 'Choose your',
    'pricing.hero_title_accent': 'Digital Pack',
    'pricing.subtitle': 'Solutions for every budget. One-time setup of 890.- then monthly subscription with no commitment.',
    'pricing.setup': 'One-time setup',
    'pricing.month': '/month',
    'pricing.choose': 'Choose',
    'pricing.popular': 'Most popular',
    'pricing.faq_title': 'Frequently asked questions',
    'pricing.faq_badge': 'FAQ',
    'pricing.start_btn': 'Start my project',
    'pricing.contact_btn': 'Contact our team',
    'pricing.custom_need': 'Need a custom solution?',
    'pricing.custom_link': 'Contact us for a personalized quote →',
    'pricing.setup_title': 'About the CHF 890.- setup',
    'pricing.setup_desc': 'The setup fee is a one-time investment covering the complete creation of your website: custom design, development, technical configuration, launch, and initial training.',
    'pricing.setup_f1': 'Custom design for your brand',
    'pricing.setup_f2': 'Development & integration',
    'pricing.setup_f3': 'Hosting & domain configuration',
    'pricing.setup_f4': 'Usage training',
    'pricing.setup_f5': 'Testing & performance optimization',
    'pricing.setup_f6': 'Launch & verification',
    'pricing.cta_title': 'Ready to start?',
    'pricing.cta_sub': 'Free consultation to choose the pack suited to your situation.',
    'pricing.pack1_title': 'Site Without Maintenance',
    'pricing.pack1_subtitle': 'Perfect to get started',
    'pricing.pack1_ideal': 'Ideal for freelancers and small structures who want a professional showcase.',
    'pricing.pack1_cta': 'Choose Pack 1',
    'pricing.pack1_f1': 'Professional website (up to 5 pages)',
    'pricing.pack1_f2': 'Mobile & tablet responsive design',
    'pricing.pack1_f3': 'Swiss hosting included',
    'pricing.pack1_f4': 'Domain name .ch or .com',
    'pricing.pack1_f5': 'SSL certificate (HTTPS)',
    'pricing.pack1_f6': 'Contact form',
    'pricing.pack1_f7': 'Google Maps integration',
    'pricing.pack1_f8': 'Content updates',
    'pricing.pack1_f9': 'Technical maintenance',
    'pricing.pack1_f10': 'Optimized SEO',
    'pricing.pack1_f11': 'Priority support',
    'pricing.pack1_f12': 'Marketing Solutions',
    'pricing.pack2_title': 'Site + Maintenance',
    'pricing.pack2_subtitle': 'Most popular',
    'pricing.pack2_ideal': 'Ideal for SMEs who want a continuously maintained and optimized digital presence.',
    'pricing.pack2_cta': 'Choose Pack 2',
    'pricing.pack2_f1': 'Professional website (up to 10 pages)',
    'pricing.pack2_f2': 'Mobile & tablet responsive design',
    'pricing.pack2_f3': 'Swiss hosting included',
    'pricing.pack2_f4': 'Domain name .ch or .com',
    'pricing.pack2_f5': 'SSL certificate (HTTPS)',
    'pricing.pack2_f6': 'Advanced contact form',
    'pricing.pack2_f7': 'Google Maps integration',
    'pricing.pack2_f8': 'Content updates (2x/month)',
    'pricing.pack2_f9': 'Technical maintenance',
    'pricing.pack2_f10': 'Basic SEO',
    'pricing.pack2_f11': 'Priority support (48h)',
    'pricing.pack2_f12': 'Marketing Solutions',
    'pricing.pack3_title': 'Site + Marketing',
    'pricing.pack3_subtitle': 'Complete solution',
    'pricing.pack3_ideal': 'Ideal for ambitious companies who want to generate leads and grow quickly.',
    'pricing.pack3_cta': 'Choose Pack 3',
    'pricing.pack3_f1': 'Professional website (unlimited pages)',
    'pricing.pack3_f2': 'Mobile & tablet responsive design',
    'pricing.pack3_f3': 'Swiss hosting included',
    'pricing.pack3_f4': 'Domain name .ch or .com',
    'pricing.pack3_f5': 'SSL certificate (HTTPS)',
    'pricing.pack3_f6': 'Advanced forms + CRM',
    'pricing.pack3_f7': 'Google Maps integration',
    'pricing.pack3_f8': 'Unlimited updates',
    'pricing.pack3_f9': 'Complete technical maintenance',
    'pricing.pack3_f10': 'Advanced SEO + monthly report',
    'pricing.pack3_f11': 'Dedicated support (24h)',
    'pricing.pack3_f12': 'Google Ads + Social Media + Email',
    'pricing.faq1_q': 'Can I change packs after subscribing?',
    'pricing.faq1_a': 'Yes, you can upgrade or downgrade your pack at any time. The change takes effect at the next billing cycle.',
    'pricing.faq2_q': 'Is the 890.- setup mandatory?',
    'pricing.faq2_a': 'Yes, the one-time setup of CHF 890.- covers the creation, design and launch of your site. It is paid only once.',
    'pricing.faq3_q': 'Can I cancel at any time?',
    'pricing.faq3_a': "Yes, our subscriptions have no long-term commitment. You can cancel with one month's notice.",
    'pricing.faq4_q': 'What happens to my site if I cancel?',
    'pricing.faq4_a': 'You keep your domain name and we provide you with an export of your site. You remain the owner of your content.',
    'pricing.faq5_q': 'Are prices in CHF?',
    'pricing.faq5_a': 'Yes, all our prices are in Swiss Francs (CHF), VAT included.',
    'pricing.faq6_q': 'Do you offer rates for associations?',
    'pricing.faq6_a': 'Yes, we offer preferential rates for associations and non-profit organizations. Contact us for a personalized quote.',
    'marketing.badge': 'Digital Marketing Solutions',
    'marketing.title': 'Boost your',
    'marketing.title_accent': 'online visibility',
    'marketing.subtitle': 'Complete digital marketing strategies to attract more customers and grow your business.',
    'marketing.cta': 'Start my marketing project',
    'marketing.cta_audit': 'Free audit of my presence',
    'marketing.partner_title': 'Certified Google Partner',
    'marketing.partner_sub': 'We master the entire Google ecosystem for your growth',
    'marketing.services_badge': 'Our services',
    'marketing.services_title': 'Everything you need',
    'marketing.services_sub': 'From local visibility to paid advertising, social media and email marketing.',
    'marketing.process_badge': 'Our process',
    'marketing.process_title': 'How it works',
    'marketing.process_1_title': 'Digital Audit',
    'marketing.process_1_desc': 'Complete analysis of your current digital presence and that of your competitors.',
    'marketing.process_2_title': 'Custom Strategy',
    'marketing.process_2_desc': 'Definition of a marketing strategy adapted to your sector, objectives and budget.',
    'marketing.process_3_title': 'Implementation',
    'marketing.process_3_desc': 'Deployment of marketing actions with weekly monitoring and continuous optimizations.',
    'marketing.process_4_title': 'Measurement & Optimization',
    'marketing.process_4_desc': 'Monthly reporting with key KPIs and strategic adjustments to maximize ROI.',
    'marketing.results_badge': 'Concrete results',
    'marketing.results_title': 'Numbers that speak for themselves',
    'marketing.results_sub': 'Our Swiss SME clients see measurable results from the first months. Average performance over 6 months.',
    'marketing.cta_section_title': 'Ready to boost your marketing?',
    'marketing.cta_section_sub': 'Free digital audit of your current presence. No commitment.',
    'marketing.cta_audit_free': 'Request my free audit',
    'marketing.cta_submit': 'Submit my project',
    'ai.badge': 'AI & Automation',
    'ai.title': 'Automate your',
    'ai.title_accent': 'growth',
    'ai.subtitle': 'Artificial intelligence at the service of your business. Save time, reduce costs and increase your conversions.',
    'ai.cta': 'Discover our AI solutions',
    'ai.cta_demo': 'Request an AI demo',
    'ai.cta_pricing': 'View pricing',
    'ai.chatbot_label': 'ECR360 Chatbot — Live on your site',
    'ai.chatbot_active': 'Active 24/7',
    'ai.solutions_badge': 'AI Solutions',
    'ai.solutions_title': 'Everything AI can do for you',
    'ai.usecases_badge': 'Use cases',
    'ai.usecases_title': 'AI in your sector',
    'ai.roi_badge': 'Estimated ROI',
    'ai.roi_title': 'How much can AI save you?',
    'ai.roi_sub': 'On average, our SME clients save',
    'ai.roi_highlight': '23h per week',
    'ai.roi_sub2': 'thanks to AI automation.',
    'ai.roi_stat1_label': 'Saved/week',
    'ai.roi_stat1_sub': 'In repetitive tasks',
    'ai.roi_stat2_label': 'Resolution rate',
    'ai.roi_stat2_sub': 'AI Chatbot',
    'ai.roi_stat3_label': 'More conversions',
    'ai.roi_stat3_sub': 'On automated leads',
    'ai.cta_section_title': 'Ready to automate your business?',
    'ai.cta_section_sub': 'Free 30-minute demo to see AI in action in your sector.',
    'ai.cta_demo_free': 'Request my free demo',
    'ai.sol1_title': 'Intelligent Chatbot',
    'ai.sol1_desc': "Automatically answer your visitors' frequent questions 24/7. Qualify leads before they contact you.",
    'ai.sol1_benefit': 'Save 15h/week on customer support',
    'ai.sol1_tag': 'Conversational AI',
    'ai.sol2_title': 'Email Automation',
    'ai.sol2_desc': "Automatic email sequences triggered by your prospects' actions. Intelligent nurturing until conversion.",
    'ai.sol2_benefit': '4x more conversions on cold leads',
    'ai.sol2_tag': 'Marketing Automation',
    'ai.sol3_title': 'AI Lead Scoring',
    'ai.sol3_desc': 'AI analyzes your visitors behavior and scores each lead by conversion probability. Focus on the best prospects.',
    'ai.sol3_benefit': '60% reduction in qualification time',
    'ai.sol3_tag': 'Artificial Intelligence',
    'ai.sol4_title': 'Automated Reporting',
    'ai.sol4_desc': 'Performance reports generated automatically every week. Actionable insights without any effort.',
    'ai.sol4_benefit': '3x faster decisions based on data',
    'ai.sol4_tag': 'Business Intelligence',
    'ai.sol5_title': 'Integrations & CRM',
    'ai.sol5_desc': 'Connect your site to your CRM, calendar, and business tools. Everything synchronized automatically, zero manual entry.',
    'ai.sol5_benefit': '8h/week saved on administrative tasks',
    'ai.sol5_tag': 'Automation',
    'ai.sol6_title': 'AI Content Generation',
    'ai.sol6_desc': 'AI generates text variants for your ads, emails and social posts. Test faster, optimize continuously.',
    'ai.sol6_benefit': '10x more variations tested per month',
    'ai.sol6_tag': 'Generative AI',
    'ai.uc1_sector': 'Craftsmen & SMEs',
    'ai.uc1_scenario': 'A plumber automatically receives quote requests via chatbot, qualified by AI, with instant SMS notification.',
    'ai.uc1_saving': '12h/week saved',
    'ai.uc2_sector': 'Liberal professionals',
    'ai.uc2_scenario': 'A medical practice automates appointment reminders, patient follow-ups and monthly newsletters.',
    'ai.uc2_saving': '8h/week saved',
    'ai.uc3_sector': 'Commerce & Retail',
    'ai.uc3_scenario': 'A shop sends automatic follow-up emails after cart abandonment, with personalized offers by AI.',
    'ai.uc3_saving': '+35% cart recovery',
    'contact.badge': 'Contact us',
    'contact.title': "Let's talk about your",
    'contact.title_accent': 'project',
    'contact.subtitle': 'Our team is available to answer all your questions and support you in your digital transformation.',
    'contact.firstname': 'First name',
    'contact.lastname': 'Last name',
    'contact.email': 'Email',
    'contact.company': 'Company',
    'contact.phone': 'Phone',
    'contact.pack': 'Desired pack',
    'contact.message': 'Message',
    'contact.send': 'Send message',
    'contact.sending': 'Sending...',
    'contact.success_title': 'Message sent!',
    'contact.success_sub': 'Thank you for your message. Our team will reply within 48 hours.',
    'contact.back_home': 'Back to home',
    'contact.form_title': 'Send us your request',
    'contact.form_sub': 'All fields marked * are required.',
    'contact.message_placeholder': 'Tell us about your company, your digital goals and what you are looking for...',
    'contact.privacy': 'By submitting this form, you agree that your data will be used to contact you about your project. In accordance with Swiss DPA, you can request deletion of your data at any time.',
    'contact.coords_title': 'Our contact details',
    'contact.next_title': 'What happens next',
    'contact.next_1': 'We analyze your request and your sector',
    'contact.next_2': '30-min call to understand your goals',
    'contact.next_3': 'Personalized proposal with transparent quote',
    'contact.next_4': 'Project start within 5 business days',
    'contact.why_title': 'Why ECR360 Digital?',
    'contact.why_1': '150+ sites delivered in Switzerland',
    'contact.why_2': '100% Swiss team, French-speaking support',
    'contact.why_3': 'Transparent pricing, no surprises',
    'contact.why_4': 'Monthly cancellation without penalty',
    'contact.why_5': 'Free initial consultation',
    'contact.packs_title': 'Our offers reminder',
    'contact.packs_link': 'View full pack comparison →',
    'contact.pack_opt_default': 'Desired pack',
    'contact.pack_opt_1': 'Pack 1 — Site Without Maintenance (19.90.-/month)',
    'contact.pack_opt_2': 'Pack 2 — Site + Maintenance (39.90.-/month)',
    'contact.pack_opt_3': 'Pack 3 — Site + Marketing (99.90.-/month)',
    'contact.pack_opt_marketing': 'Digital Marketing only',
    'contact.pack_opt_ai': 'AI Automation only',
    'contact.pack_opt_custom': 'Custom solution',
    'contact.info_email': 'Email',
    'contact.info_website': 'Website',
    'contact.info_location': 'Location',
    'contact.info_response': 'Response',
    'contact.info_response_val': 'Within 48 hours',
    'contact.pack_reminder_chf': 'CHF/month',
    'demande.badge': 'Project Request',
    'demande.title': 'Start your',
    'demande.title_accent': 'digital project',
    'demande.subtitle': 'Fill in this form and our team will contact you within 48h to discuss your project and propose the best solution.',
    'demande.step1': 'Your information',
    'demande.step2': 'Site type',
    'demande.step3': 'Chosen pack',
    'demande.step4': 'Additional services',
    'demande.step4_sub': 'In addition to your website, do you need other services? Check everything that interests you.',
    'demande.step5': 'Project description',
    'demande.step6': 'Logo',
    'demande.step7': 'Color palette',
    'demande.step7_sub': 'Choose a color palette that matches the image you want to give your site.',
    'demande.step8': 'Additional information',
    'demande.optional': '(optional)',
    'demande.required_fields': '* Required fields. Your data is handled confidentially.',
    'demande.submit': 'Send my request',
    'demande.submitting': 'Sending...',
    'demande.firstname': 'First name',
    'demande.lastname': 'Last name',
    'demande.email': 'Email',
    'demande.phone': 'Phone',
    'demande.company': 'Company name',
    'demande.url': 'Desired URL',
    'demande.description_placeholder': 'Describe your project, your goals, your industry...',
    'demande.deadline': 'Desired deadline',
    'demande.extra_message': 'Additional message',
    'demande.extra_message_placeholder': 'Any additional information you would like to share with us...',
    'demande.logo_drop': 'Drop your logo here',
    'demande.logo_formats': 'PNG, JPG or SVG — max 5MB',
    'demande.logo_delete': 'Delete',
    'demande.logo_create': "I don't have a logo — I'd like one created",
    'demande.logo_create_sub': 'Our team will offer you logo creation options tailored to your identity.',
    'demande.palette_selected': 'Selected palette:',
    'demande.palette_clear': 'Clear',
    'demande.marketing_title': 'Digital Marketing Solutions',
    'demande.marketing_sub': 'Google, social media, online advertising',
    'demande.ai_title': 'Automation & Artificial Intelligence',
    'demande.ai_sub': 'Chatbots, automated workflows, custom AI',
    'demande.email_pro_title': 'Professional email addresses',
    'demande.email_pro_sub': 'E.g.: contact@your-company.ch — Professional emails hosted in Switzerland with your domain name.',
    'demande.success_title': 'Request sent!',
    'demande.success_sub': 'Your project request has been received. Our team will contact you within',
    'demande.success_sub2': '48 hours',
    'demande.success_sub3': 'to discuss your project.',
    'demande.pack_label': 'Chosen pack',
    'demande.site_type_label': 'Site type',
    'demande.marketing_count': 'service(s)',
    'demande.ai_count': 'service(s)',
    'demande.back_home': 'Back to home',
    'demande.see_offers': 'See our offers',
    'demande.type_vitrine': 'Showcase Site',
    'demande.type_vitrine_desc': 'Presentation of your company',
    'demande.type_ecommerce': 'E-Commerce',
    'demande.type_ecommerce_desc': 'Online store with payments',
    'demande.type_portfolio': 'Portfolio',
    'demande.type_portfolio_desc': 'Showcase of your work',
    'demande.type_autre': 'Other',
    'demande.type_autre_desc': 'Other type of project',
    'demande.pack_essentiel': 'Essential Pack',
    'demande.pack_essentiel_price': '890.- + 19.90.-/month',
    'demande.pack_essentiel_desc': 'Professional site without maintenance',
    'demande.pack_confort': 'Comfort Pack',
    'demande.pack_confort_price': '890.- + 39.90.-/month',
    'demande.pack_confort_desc': 'Site + monthly maintenance',
    'demande.pack_croissance': 'Growth Pack',
    'demande.pack_croissance_price': '890.- + 99.90.-/month',
    'demande.pack_croissance_desc': 'Site + complete marketing',
    'demande.mkt_google_ads': 'Google Ads',
    'demande.mkt_google_ads_desc': 'Paid advertising campaigns on Google',
    'demande.mkt_seo': 'SEO / Search Engine Optimization',
    'demande.mkt_seo_desc': "Optimization to appear on Google's 1st page",
    'demande.mkt_gmb': 'Google My Business',
    'demande.mkt_gmb_desc': 'Google Maps listing and customer reviews',
    'demande.mkt_social': 'Social Media',
    'demande.mkt_social_desc': 'Instagram, Facebook, LinkedIn management',
    'demande.mkt_meta': 'Meta / LinkedIn Ads',
    'demande.mkt_meta_desc': 'Ads on Facebook, Instagram or LinkedIn',
    'demande.mkt_email': 'Email Marketing',
    'demande.mkt_email_desc': 'Newsletters and automated email campaigns',
    'demande.ai_chatbot': 'AI Chatbot',
    'demande.ai_chatbot_desc': 'Intelligent virtual assistant on your site',
    'demande.ai_auto': 'Task Automation',
    'demande.ai_auto_desc': 'Automated workflows (quotes, emails, CRM)',
    'demande.ai_data': 'AI Data Analysis',
    'demande.ai_data_desc': 'Dashboards and intelligent reports',
    'demande.ai_content': 'AI Content Generation',
    'demande.ai_content_desc': 'Texts, product descriptions, blog articles',
    'demande.palette_bleu': 'Professional Blue',
    'demande.palette_bleu_desc': 'Trust, seriousness, corporate',
    'demande.palette_vert': 'Nature Green',
    'demande.palette_vert_desc': 'Ecology, health, wellness',
    'demande.palette_rouge': 'Energy Red',
    'demande.palette_rouge_desc': 'Dynamism, passion, urgency',
    'demande.palette_violet': 'Creative Purple',
    'demande.palette_violet_desc': 'Creativity, luxury, innovation',
    'demande.palette_orange': 'Warm Orange',
    'demande.palette_orange_desc': 'Friendliness, energy, craftsmanship',
    'demande.palette_gris': 'Modern Grey',
    'demande.palette_gris_desc': 'Elegance, minimalism, tech',
    'demande.palette_rose': 'Elegant Pink',
    'demande.palette_rose_desc': 'Beauty, fashion, lifestyle',
    'demande.palette_jaune': 'Optimistic Yellow',
    'demande.palette_jaune_desc': 'Youth, creativity, sunshine',
    'demande.palette_turquoise': 'Fresh Turquoise',
    'demande.palette_turquoise_desc': 'Modernity, freshness, digital',
    'demande.palette_noir': 'Luxury Black',
    'demande.palette_noir_desc': 'Prestige, high-end, exclusivity',
    'demande.palette_multi': 'Multicolor / Vivid',
    'demande.palette_multi_desc': 'Creative, festive, diversity',
    'demande.palette_none': 'No preference',
    'demande.palette_none_desc': 'Let our team choose for you',
    'confirmation.title': 'Request sent',
    'confirmation.title_accent': 'successfully!',
    'confirmation.thanks': 'Thank you',
    'confirmation.thanks_sub': ', your request has been received.',
    'confirmation.summary': 'Summary of your request',
    'confirmation.next_steps': 'Next steps',
    'confirmation.back_home': 'Back to home',
    'confirmation.new_request': 'New request',
    'confirmation.full_name': 'Full name',
    'confirmation.email': 'Email',
    'confirmation.company': 'Company',
    'confirmation.pack': 'Chosen pack',
    'confirmation.services': 'Selected services',
    'confirmation.see_offers': 'View our offers',
    'login.title': 'Login',
    'login.subtitle': 'Access your client area',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.forgot': 'Forgot password?',
    'login.submit': 'Sign in',
    'login.loading': 'Signing in...',
    'login.no_account': 'No account yet?',
    'login.signup': 'Create an account',
    'signup.title': 'Create an account',
    'signup.subtitle': 'Join the ECR360 client area',
    'signup.company': 'Company name',
    'signup.email': 'Email',
    'signup.password': 'Password',
    'signup.confirm_password': 'Confirm password',
    'signup.submit': 'Create my account',
    'signup.loading': 'Creating...',
    'signup.already': 'Already have an account?',
    'signup.login': 'Sign in',
    'reset.title': 'Forgot password',
    'reset.subtitle': 'Enter your email to receive a reset link.',
    'reset.email': 'Email',
    'reset.submit': 'Send link',
    'reset.loading': 'Sending...',
    'reset.success': 'Email sent! Check your inbox.',
    'reset.back_login': 'Back to login',
    'client.dashboard': 'Dashboard',
    'client.subscription': 'Subscription',
    'client.invoices': 'Invoices',
    'client.profile': 'Profile',
    'client.logout': 'Logout',
    'client.welcome': 'Welcome',
  },

  de: {
    'nav.home': 'Startseite',
    'nav.pricing': 'Angebote & Preise',
    'nav.marketing': 'Marketing',
    'nav.ai': 'KI & Automatisierung',
    'nav.contact': 'Kontakt',
    'nav.espace_client': 'Kundenbereich',
    'nav.start_project': 'Mein Projekt starten',
    'home.badge': '🇨🇭 Schweizer Digitallösungen',
    'home.hero_title': 'Ihre digitale Präsenz,',
    'home.hero_title_accent': 'schlüsselfertig',
    'home.hero_subtitle': 'Professionelle Websites, digitales Marketing und KI-Automatisierung für Schweizer KMU. Ab',
    'home.hero_subtitle_setup': '890.- Setup',
    'home.hero_subtitle_and': 'und',
    'home.hero_subtitle_monthly': '19.90.-/Monat',
    'home.cta_offers': 'Angebote ansehen',
    'home.cta_expert': 'Mit einem Experten sprechen',
    'home.trust_no_fees': 'Keine versteckten Kosten',
    'home.trust_support': 'Support auf Französisch',
    'home.trust_cancel': 'Monatliche Kündigung',
    'home.scroll_discover': 'Entdecken',
    'home.stats_sites': 'Gelieferte Websites',
    'home.stats_clients': 'Zufriedene Kunden',
    'home.stats_delay': 'Reaktionszeit',
    'home.stats_packs': 'Verfügbare Pakete',
    'home.problem_badge': 'Das Problem',
    'home.problem_title': 'Ihr Unternehmen verdient mehr als eine',
    'home.problem_title_strike': 'amateurhafte',
    'home.problem_sub': 'In der Schweiz verlieren 67% der KMU Kunden aufgrund fehlender professioneller digitaler Präsenz. Währenddessen gewinnen Ihre Konkurrenten diese Kunden online.',
    'home.problem_1': 'Website nicht auf Google auffindbar',
    'home.problem_2': 'Design nicht für Mobilgeräte angepasst',
    'home.problem_3': 'Kein Lead-Erfassungssystem',
    'home.problem_4': 'Kein automatisiertes Marketing',
    'home.solution_badge': 'Unsere Lösung',
    'home.solution_title': 'Eine vollständige Lösung,',
    'home.solution_title_accent': 'ohne Komplexität',
    'home.solution_sub': 'ECR360 Digital übernimmt Ihre digitale Präsenz von A bis Z. Sie konzentrieren sich auf Ihr Geschäft, wir kümmern uns um den Rest.',
    'home.solution_1': 'Professionelle Website in 5 Tagen geliefert',
    'home.solution_2': 'Google-SEO von Anfang an optimiert',
    'home.solution_3': 'Integrierte Marketing-Automatisierung',
    'home.solution_4': 'Support auf Französisch, Schweizer Team',
    'home.packs_title': 'Unsere',
    'home.packs_title_accent': 'Pakete',
    'home.packs_section_badge': 'Unsere Pakete',
    'home.packs_section_title': '3 Pakete für',
    'home.packs_section_accent': 'jeden Ehrgeiz',
    'home.packs_section_sub': 'Wählen Sie das Paket, das zu Ihrer Situation passt. Jederzeit upgraden.',
    'home.packs_compare': 'Detaillierten Vergleich ansehen →',
    'home.pack1_title': 'Website ohne Wartung',
    'home.pack2_title': 'Website + Wartung',
    'home.pack3_title': 'Website + Marketing',
    'home.pack_setup': 'Einmalige Einrichtung',
    'home.pack_month': '/Monat',
    'home.pack_choose': 'Wählen',
    'home.pack_popular': 'Am beliebtesten',
    'home.pack1_f1': 'Professionelle Website',
    'home.pack1_f2': 'Mobiles responsives Design',
    'home.pack1_f3': 'Hosting inklusive',
    'home.pack1_f4': 'Domainname (.ch)',
    'home.pack1_f5': 'SSL-Zertifikat',
    'home.pack1_f6': 'Kontaktformular',
    'home.pack2_f1': 'Alles aus Paket 1',
    'home.pack2_f2': 'Monatliche Updates',
    'home.pack2_f3': 'Technische Wartung',
    'home.pack2_f4': 'Automatische Backups',
    'home.pack2_f5': 'Prioritätssupport',
    'home.pack2_f6': 'Monatlicher Bericht',
    'home.pack3_f1': 'Alles aus Paket 2',
    'home.pack3_f2': 'Lokale SEO-Strategie',
    'home.pack3_f3': 'Social-Media-Management',
    'home.pack3_f4': 'Google Ads Kampagnen',
    'home.pack3_f5': 'E-Mail-Marketing',
    'home.pack3_f6': 'Analytics-Dashboard',
    'home.services_badge': 'Unsere Dienste',
    'home.services_title': 'Mehr als Websites —',
    'home.services_title_accent': 'ein digitales Ökosystem',
    'home.services_marketing_title': 'Digitale Marketinglösungen',
    'home.services_marketing_desc': 'Lokales SEO, Google Ads, soziale Netzwerke, E-Mail-Marketing. Alles, was Sie brauchen, um Kunden online zu gewinnen.',
    'home.services_marketing_discover': 'Entdecken →',
    'home.services_ai_title': 'KI-Automatisierung',
    'home.services_ai_desc': 'Chatbots, E-Mail-Automatisierung, Lead-Qualifizierung. KI im Dienst Ihres Wachstums.',
    'home.services_ai_explore': 'KI erkunden →',
    'home.services_support_title': 'Schweizer Support',
    'home.services_support_desc': 'Team in der Schweiz, Support auf Französisch, 48h-Antwort garantiert.',
    'home.services_speed_title': 'Lieferung in 5 Tagen',
    'home.services_speed_desc': 'Ihre professionelle Website in weniger als einer Woche online.',
    'home.services_analytics_title': 'Analytics inklusive',
    'home.services_analytics_desc': 'Klares Dashboard zur Verfolgung Ihrer Leistung in Echtzeit.',
    'home.testimonials_title': 'Was unsere',
    'home.testimonials_title_accent': 'Kunden sagen',
    'home.testimonials_badge': 'Referenzen',
    'home.t1_quote': 'ECR360 Digital hat unsere Online-Präsenz transformiert. In 3 Monaten stiegen unsere Angebotsanfragen um 60%. Tadelloser Service, reaktionsfähiges Team.',
    'home.t1_role': 'Geschäftsführer, Dubois Sanitär AG',
    'home.t2_quote': 'Paket 3 ermöglichte uns, unser Marketing zu automatisieren. Wir erhalten jetzt jede Woche qualifizierte Leads ohne eigenen Aufwand.',
    'home.t2_role': 'Direktorin, Favre HR-Büro',
    'home.t3_quote': 'Unschlagbares Preis-Leistungs-Verhältnis für die Schweiz. Unsere Website ist wunderschön und der Support ist immer verfügbar. Sehr empfehlenswert.',
    'home.t3_role': 'Gründer, Müller Consulting',
    'home.cta_section_title': 'Bereit, Ihr',
    'home.cta_section_accent': 'digitales Projekt zu starten?',
    'home.cta_section_sub': 'Schliessen Sie sich über 150 Schweizer KMU an, die uns vertrauen.',
    'home.cta_section_btn': 'Mein Projekt starten',
    'home.cta_section_btn2': 'Angebote ansehen',
    'home.cta_section_note': '100% kostenlose Beratung · Antwort innerhalb 48h · Keine Bindung',
    'pricing.title': 'Unsere',
    'pricing.title_accent': 'Angebote & Preise',
    'pricing.hero_title': 'Wählen Sie Ihr',
    'pricing.hero_title_accent': 'Digitales Paket',
    'pricing.subtitle': 'Lösungen für jedes Budget. Einmalige Einrichtung von 890.- dann monatliches Abonnement ohne Bindung.',
    'pricing.setup': 'Einmalige Einrichtung',
    'pricing.month': '/Monat',
    'pricing.choose': 'Wählen',
    'pricing.popular': 'Am beliebtesten',
    'pricing.faq_title': 'Häufig gestellte Fragen',
    'pricing.faq_badge': 'FAQ',
    'pricing.start_btn': 'Mein Projekt starten',
    'pricing.contact_btn': 'Unser Team kontaktieren',
    'pricing.custom_need': 'Benötigen Sie eine massgeschneiderte Lösung?',
    'pricing.custom_link': 'Kontaktieren Sie uns für ein individuelles Angebot →',
    'pricing.setup_title': 'Über die CHF 890.- Einrichtungsgebühr',
    'pricing.setup_desc': 'Die Einrichtungsgebühr ist eine einmalige Investition, die die vollständige Erstellung Ihrer Website abdeckt: massgeschneidertes Design, Entwicklung, technische Konfiguration, Launch und Erstschulung.',
    'pricing.setup_f1': 'Individuelles Design für Ihre Marke',
    'pricing.setup_f2': 'Entwicklung & Integration',
    'pricing.setup_f3': 'Hosting- & Domain-Konfiguration',
    'pricing.setup_f4': 'Nutzungsschulung',
    'pricing.setup_f5': 'Tests & Leistungsoptimierung',
    'pricing.setup_f6': 'Launch & Überprüfung',
    'pricing.cta_title': 'Bereit zu starten?',
    'pricing.cta_sub': 'Kostenlose Beratung zur Auswahl des passenden Pakets.',
    'pricing.pack1_title': 'Website ohne Wartung',
    'pricing.pack1_subtitle': 'Perfekt zum Einstieg',
    'pricing.pack1_ideal': 'Ideal für Freiberufler und kleine Strukturen, die eine professionelle Visitenkarte wollen.',
    'pricing.pack1_cta': 'Paket 1 wählen',
    'pricing.pack1_f1': 'Professionelle Website (bis zu 5 Seiten)',
    'pricing.pack1_f2': 'Responsives Design für Mobil & Tablet',
    'pricing.pack1_f3': 'Schweizer Hosting inklusive',
    'pricing.pack1_f4': 'Domainname .ch oder .com',
    'pricing.pack1_f5': 'SSL-Zertifikat (HTTPS)',
    'pricing.pack1_f6': 'Kontaktformular',
    'pricing.pack1_f7': 'Google Maps Integration',
    'pricing.pack1_f8': 'Inhaltsaktualisierungen',
    'pricing.pack1_f9': 'Technische Wartung',
    'pricing.pack1_f10': 'Optimiertes SEO',
    'pricing.pack1_f11': 'Prioritätssupport',
    'pricing.pack1_f12': 'Marketinglösungen',
    'pricing.pack2_title': 'Website + Wartung',
    'pricing.pack2_subtitle': 'Am beliebtesten',
    'pricing.pack2_ideal': 'Ideal für KMU, die eine kontinuierlich gepflegte und optimierte digitale Präsenz wollen.',
    'pricing.pack2_cta': 'Paket 2 wählen',
    'pricing.pack2_f1': 'Professionelle Website (bis zu 10 Seiten)',
    'pricing.pack2_f2': 'Responsives Design für Mobil & Tablet',
    'pricing.pack2_f3': 'Schweizer Hosting inklusive',
    'pricing.pack2_f4': 'Domainname .ch oder .com',
    'pricing.pack2_f5': 'SSL-Zertifikat (HTTPS)',
    'pricing.pack2_f6': 'Erweitertes Kontaktformular',
    'pricing.pack2_f7': 'Google Maps Integration',
    'pricing.pack2_f8': 'Inhaltsaktualisierungen (2x/Monat)',
    'pricing.pack2_f9': 'Technische Wartung',
    'pricing.pack2_f10': 'Basis-SEO',
    'pricing.pack2_f11': 'Prioritätssupport (48h)',
    'pricing.pack2_f12': 'Marketinglösungen',
    'pricing.pack3_title': 'Website + Marketing',
    'pricing.pack3_subtitle': 'Komplettlösung',
    'pricing.pack3_ideal': 'Ideal für ehrgeizige Unternehmen, die Leads generieren und schnell wachsen wollen.',
    'pricing.pack3_cta': 'Paket 3 wählen',
    'pricing.pack3_f1': 'Professionelle Website (unbegrenzte Seiten)',
    'pricing.pack3_f2': 'Responsives Design für Mobil & Tablet',
    'pricing.pack3_f3': 'Schweizer Hosting inklusive',
    'pricing.pack3_f4': 'Domainname .ch oder .com',
    'pricing.pack3_f5': 'SSL-Zertifikat (HTTPS)',
    'pricing.pack3_f6': 'Erweiterte Formulare + CRM',
    'pricing.pack3_f7': 'Google Maps Integration',
    'pricing.pack3_f8': 'Unbegrenzte Updates',
    'pricing.pack3_f9': 'Vollständige technische Wartung',
    'pricing.pack3_f10': 'Erweitertes SEO + Monatsbericht',
    'pricing.pack3_f11': 'Dedizierter Support (24h)',
    'pricing.pack3_f12': 'Google Ads + Social Media + E-Mail',
    'pricing.faq1_q': 'Kann ich nach der Anmeldung das Paket wechseln?',
    'pricing.faq1_a': 'Ja, Sie können Ihr Paket jederzeit upgraden oder downgraden. Die Änderung tritt beim nächsten Abrechnungszyklus in Kraft.',
    'pricing.faq2_q': 'Ist die Einrichtungsgebühr von 890.- obligatorisch?',
    'pricing.faq2_a': 'Ja, die einmalige Einrichtungsgebühr von CHF 890.- deckt die Erstellung, das Design und die Inbetriebnahme Ihrer Website ab. Sie wird nur einmal bezahlt.',
    'pricing.faq3_q': 'Kann ich jederzeit kündigen?',
    'pricing.faq3_a': 'Ja, unsere Abonnements haben keine langfristige Bindung. Sie können mit einer Frist von einem Monat kündigen.',
    'pricing.faq4_q': 'Was passiert mit meiner Website, wenn ich kündige?',
    'pricing.faq4_a': 'Sie behalten Ihren Domainnamen und wir stellen Ihnen einen Export Ihrer Website zur Verfügung. Sie bleiben Eigentümer Ihrer Inhalte.',
    'pricing.faq5_q': 'Sind die Preise in CHF?',
    'pricing.faq5_a': 'Ja, alle unsere Preise sind in Schweizer Franken (CHF), inkl. MwSt.',
    'pricing.faq6_q': 'Bieten Sie Tarife für Vereine an?',
    'pricing.faq6_a': 'Ja, wir bieten Vorzugstarife für Vereine und gemeinnützige Organisationen an. Kontaktieren Sie uns für ein individuelles Angebot.',
    'marketing.badge': 'Digitale Marketinglösungen',
    'marketing.title': 'Steigern Sie Ihre',
    'marketing.title_accent': 'Online-Sichtbarkeit',
    'marketing.subtitle': 'Vollständige digitale Marketingstrategien, um mehr Kunden zu gewinnen und Ihr Geschäft auszubauen.',
    'marketing.cta': 'Mein Marketingprojekt starten',
    'marketing.cta_audit': 'Kostenloser Audit meiner Präsenz',
    'marketing.partner_title': 'Zertifizierter Google-Partner',
    'marketing.partner_sub': 'Wir beherrschen das gesamte Google-Ökosystem für Ihr Wachstum',
    'marketing.services_badge': 'Unsere Dienste',
    'marketing.services_title': 'Alles, was Sie brauchen',
    'marketing.services_sub': 'Von lokaler Sichtbarkeit bis zu bezahlter Werbung, sozialen Netzwerken und E-Mail-Marketing.',
    'marketing.process_badge': 'Unser Prozess',
    'marketing.process_title': 'Wie es funktioniert',
    'marketing.process_1_title': 'Digitaler Audit',
    'marketing.process_1_desc': 'Vollständige Analyse Ihrer aktuellen digitalen Präsenz und der Ihrer Mitbewerber.',
    'marketing.process_2_title': 'Massgeschneiderte Strategie',
    'marketing.process_2_desc': 'Definition einer Marketingstrategie, die auf Ihren Sektor, Ihre Ziele und Ihr Budget zugeschnitten ist.',
    'marketing.process_3_title': 'Umsetzung',
    'marketing.process_3_desc': 'Einsatz von Marketingmassnahmen mit wöchentlicher Überwachung und kontinuierlichen Optimierungen.',
    'marketing.process_4_title': 'Messung & Optimierung',
    'marketing.process_4_desc': 'Monatliches Reporting mit wichtigen KPIs und strategischen Anpassungen zur ROI-Maximierung.',
    'marketing.results_badge': 'Konkrete Ergebnisse',
    'marketing.results_title': 'Zahlen, die für sich sprechen',
    'marketing.results_sub': 'Unsere Schweizer KMU-Kunden sehen messbare Ergebnisse ab den ersten Monaten. Durchschnittliche Leistung über 6 Monate.',
    'marketing.cta_section_title': 'Bereit, Ihr Marketing zu boosten?',
    'marketing.cta_section_sub': 'Kostenloser digitaler Audit Ihrer aktuellen Präsenz. Ohne Verpflichtung.',
    'marketing.cta_audit_free': 'Meinen kostenlosen Audit anfordern',
    'marketing.cta_submit': 'Mein Projekt einreichen',
    'ai.badge': 'KI & Automatisierung',
    'ai.title': 'Automatisieren Sie Ihr',
    'ai.title_accent': 'Wachstum',
    'ai.subtitle': 'Künstliche Intelligenz im Dienst Ihres Unternehmens. Sparen Sie Zeit, senken Sie Kosten und steigern Sie Ihre Conversions.',
    'ai.cta': 'Unsere KI-Lösungen entdecken',
    'ai.cta_demo': 'KI-Demo anfordern',
    'ai.cta_pricing': 'Preise ansehen',
    'ai.chatbot_label': 'ECR360 Chatbot — Live auf Ihrer Website',
    'ai.chatbot_active': 'Aktiv 24/7',
    'ai.solutions_badge': 'KI-Lösungen',
    'ai.solutions_title': 'Alles, was KI für Sie tun kann',
    'ai.usecases_badge': 'Anwendungsfälle',
    'ai.usecases_title': 'KI in Ihrer Branche',
    'ai.roi_badge': 'Geschätzter ROI',
    'ai.roi_title': 'Wie viel kann KI Ihnen sparen?',
    'ai.roi_sub': 'Im Durchschnitt sparen unsere KMU-Kunden',
    'ai.roi_highlight': '23h pro Woche',
    'ai.roi_sub2': 'dank KI-Automatisierung.',
    'ai.roi_stat1_label': 'Gespart/Woche',
    'ai.roi_stat1_sub': 'Bei repetitiven Aufgaben',
    'ai.roi_stat2_label': 'Lösungsrate',
    'ai.roi_stat2_sub': 'KI-Chatbot',
    'ai.roi_stat3_label': 'Mehr Conversions',
    'ai.roi_stat3_sub': 'Bei automatisierten Leads',
    'ai.cta_section_title': 'Bereit, Ihr Business zu automatisieren?',
    'ai.cta_section_sub': 'Kostenlose 30-minütige Demo, um KI in Ihrer Branche in Aktion zu sehen.',
    'ai.cta_demo_free': 'Meine kostenlose Demo anfordern',
    'ai.sol1_title': 'Intelligenter Chatbot',
    'ai.sol1_desc': 'Beantworten Sie automatisch die häufigen Fragen Ihrer Besucher 24/7. Qualifizieren Sie Leads, bevor sie Sie kontaktieren.',
    'ai.sol1_benefit': '15h/Woche Kundensupport sparen',
    'ai.sol1_tag': 'Konversations-KI',
    'ai.sol2_title': 'E-Mail-Automatisierung',
    'ai.sol2_desc': 'Automatische E-Mail-Sequenzen, ausgelöst durch die Aktionen Ihrer Interessenten. Intelligentes Nurturing bis zur Conversion.',
    'ai.sol2_benefit': '4x mehr Conversions bei kalten Leads',
    'ai.sol2_tag': 'Marketing-Automatisierung',
    'ai.sol3_title': 'KI-Lead-Scoring',
    'ai.sol3_desc': 'KI analysiert das Verhalten Ihrer Besucher und bewertet jeden Lead nach seiner Conversion-Wahrscheinlichkeit.',
    'ai.sol3_benefit': '60% Reduzierung der Qualifizierungszeit',
    'ai.sol3_tag': 'Künstliche Intelligenz',
    'ai.sol4_title': 'Automatisiertes Reporting',
    'ai.sol4_desc': 'Leistungsberichte werden automatisch jede Woche generiert. Umsetzbare Erkenntnisse ohne Aufwand.',
    'ai.sol4_benefit': '3x schnellere Entscheidungen basierend auf Daten',
    'ai.sol4_tag': 'Business Intelligence',
    'ai.sol5_title': 'Integrationen & CRM',
    'ai.sol5_desc': 'Verbinden Sie Ihre Website mit Ihrem CRM, Kalender und Geschäftstools. Alles automatisch synchronisiert.',
    'ai.sol5_benefit': '8h/Woche bei Verwaltungsaufgaben gespart',
    'ai.sol5_tag': 'Automatisierung',
    'ai.sol6_title': 'KI-Inhaltsgenerierung',
    'ai.sol6_desc': 'KI generiert Textvarianten für Ihre Anzeigen, E-Mails und Social Posts. Schneller testen, kontinuierlich optimieren.',
    'ai.sol6_benefit': '10x mehr getestete Variationen pro Monat',
    'ai.sol6_tag': 'Generative KI',
    'ai.uc1_sector': 'Handwerker & KMU',
    'ai.uc1_scenario': 'Ein Klempner erhält automatisch Angebotsanfragen über Chatbot, von KI qualifiziert, mit sofortiger SMS-Benachrichtigung.',
    'ai.uc1_saving': '12h/Woche gespart',
    'ai.uc2_sector': 'Freie Berufe',
    'ai.uc2_scenario': 'Eine Arztpraxis automatisiert Terminerinnerungen, Patientennachverfolgungen und monatliche Newsletter.',
    'ai.uc2_saving': '8h/Woche gespart',
    'ai.uc3_sector': 'Handel & Einzelhandel',
    'ai.uc3_scenario': 'Ein Geschäft sendet automatische Follow-up-E-Mails nach Warenkorbabbruch mit personalisierten KI-Angeboten.',
    'ai.uc3_saving': '+35% Warenkorbwiederherstellung',
    'contact.badge': 'Kontaktieren Sie uns',
    'contact.title': 'Sprechen wir über Ihr',
    'contact.title_accent': 'Projekt',
    'contact.subtitle': 'Unser Team steht Ihnen zur Verfügung, um alle Ihre Fragen zu beantworten und Sie bei Ihrer digitalen Transformation zu begleiten.',
    'contact.firstname': 'Vorname',
    'contact.lastname': 'Nachname',
    'contact.email': 'E-Mail',
    'contact.company': 'Unternehmen',
    'contact.phone': 'Telefon',
    'contact.pack': 'Gewünschtes Paket',
    'contact.message': 'Nachricht',
    'contact.send': 'Nachricht senden',
    'contact.sending': 'Wird gesendet...',
    'contact.success_title': 'Nachricht gesendet!',
    'contact.success_sub': 'Vielen Dank für Ihre Nachricht. Unser Team antwortet Ihnen innerhalb von 48 Stunden.',
    'contact.back_home': 'Zurück zur Startseite',
    'contact.form_title': 'Senden Sie uns Ihre Anfrage',
    'contact.form_sub': 'Alle mit * markierten Felder sind Pflichtfelder.',
    'contact.message_placeholder': 'Erzählen Sie uns von Ihrem Unternehmen, Ihren digitalen Zielen und was Sie suchen...',
    'contact.privacy': 'Durch das Absenden dieses Formulars stimmen Sie zu, dass Ihre Daten verwendet werden, um Sie bezüglich Ihres Projekts zu kontaktieren. Gemäss Schweizer DSG können Sie jederzeit die Löschung Ihrer Daten beantragen.',
    'contact.coords_title': 'Unsere Kontaktdaten',
    'contact.next_title': 'Was als nächstes passiert',
    'contact.next_1': 'Wir analysieren Ihre Anfrage und Ihren Sektor',
    'contact.next_2': '30-minütiger Anruf zum Verständnis Ihrer Ziele',
    'contact.next_3': 'Personalisiertes Angebot mit transparentem Kostenvoranschlag',
    'contact.next_4': 'Projektstart innerhalb von 5 Werktagen',
    'contact.why_title': 'Warum ECR360 Digital?',
    'contact.why_1': '150+ Websites in der Schweiz geliefert',
    'contact.why_2': '100% Schweizer Team, Support auf Französisch',
    'contact.why_3': 'Transparente Preise, keine Überraschungen',
    'contact.why_4': 'Monatliche Kündigung ohne Strafe',
    'contact.why_5': 'Kostenlose Erstberatung',
    'contact.packs_title': 'Erinnerung an unsere Angebote',
    'contact.packs_link': 'Vollständigen Paketvergleich ansehen →',
    'contact.pack_opt_default': 'Gewünschtes Paket',
    'contact.pack_opt_1': 'Paket 1 — Website ohne Wartung (19.90.-/Monat)',
    'contact.pack_opt_2': 'Paket 2 — Website + Wartung (39.90.-/Monat)',
    'contact.pack_opt_3': 'Paket 3 — Website + Marketing (99.90.-/Monat)',
    'contact.pack_opt_marketing': 'Nur digitales Marketing',
    'contact.pack_opt_ai': 'Nur KI-Automatisierung',
    'contact.pack_opt_custom': 'Massgeschneiderte Lösung',
    'contact.info_email': 'E-Mail',
    'contact.info_website': 'Website',
    'contact.info_location': 'Standort',
    'contact.info_response': 'Antwort',
    'contact.info_response_val': 'Innerhalb von 48 Stunden',
    'contact.pack_reminder_chf': 'CHF/Monat',
    'demande.badge': 'Projektanfrage',
    'demande.title': 'Starten Sie Ihr',
    'demande.title_accent': 'digitales Projekt',
    'demande.subtitle': 'Füllen Sie dieses Formular aus und unser Team wird Sie innerhalb von 48 Stunden kontaktieren.',
    'demande.step1': 'Ihre Informationen',
    'demande.step2': 'Website-Typ',
    'demande.step3': 'Gewähltes Paket',
    'demande.step4': 'Zusätzliche Dienste',
    'demande.step4_sub': 'Benötigen Sie neben Ihrer Website weitere Dienste? Wählen Sie alles aus, was Sie interessiert.',
    'demande.step5': 'Projektbeschreibung',
    'demande.step6': 'Logo',
    'demande.step7': 'Farbpalette',
    'demande.step7_sub': 'Wählen Sie eine Farbpalette, die dem Image entspricht, das Sie Ihrer Website geben möchten.',
    'demande.step8': 'Zusätzliche Informationen',
    'demande.optional': '(optional)',
    'demande.required_fields': '* Pflichtfelder. Ihre Daten werden vertraulich behandelt.',
    'demande.submit': 'Meine Anfrage senden',
    'demande.submitting': 'Wird gesendet...',
    'demande.firstname': 'Vorname',
    'demande.lastname': 'Nachname',
    'demande.email': 'E-Mail',
    'demande.phone': 'Telefon',
    'demande.company': 'Unternehmensname',
    'demande.url': 'Gewünschte URL',
    'demande.description_placeholder': 'Beschreiben Sie Ihr Projekt, Ihre Ziele, Ihre Branche...',
    'demande.deadline': 'Gewünschter Termin',
    'demande.extra_message': 'Zusätzliche Nachricht',
    'demande.extra_message_placeholder': 'Alle zusätzlichen Informationen, die Sie uns mitteilen möchten...',
    'demande.logo_drop': 'Logo hier ablegen',
    'demande.logo_formats': 'PNG, JPG oder SVG — max 5MB',
    'demande.logo_delete': 'Löschen',
    'demande.logo_create': 'Ich habe kein Logo — ich möchte eines erstellen lassen',
    'demande.logo_create_sub': 'Unser Team wird Ihnen Logo-Erstellungsoptionen anbieten, die zu Ihrer Identität passen.',
    'demande.palette_selected': 'Ausgewählte Palette:',
    'demande.palette_clear': 'Löschen',
    'demande.marketing_title': 'Digitale Marketinglösungen',
    'demande.marketing_sub': 'Google, soziale Netzwerke, Online-Werbung',
    'demande.ai_title': 'Automatisierung & Künstliche Intelligenz',
    'demande.ai_sub': 'Chatbots, automatisierte Workflows, massgeschneiderte KI',
    'demande.email_pro_title': 'Professionelle E-Mail-Adressen',
    'demande.email_pro_sub': 'Z.B.: kontakt@ihr-unternehmen.ch — Professionelle E-Mails in der Schweiz gehostet mit Ihrem Domainnamen.',
    'demande.success_title': 'Anfrage gesendet!',
    'demande.success_sub': 'Ihre Projektanfrage wurde erhalten. Unser Team wird Sie innerhalb von',
    'demande.success_sub2': '48 Stunden',
    'demande.success_sub3': 'kontaktieren.',
    'demande.pack_label': 'Gewähltes Paket',
    'demande.site_type_label': 'Website-Typ',
    'demande.marketing_count': 'Dienst(e)',
    'demande.ai_count': 'Dienst(e)',
    'demande.back_home': 'Zurück zur Startseite',
    'demande.see_offers': 'Unsere Angebote ansehen',
    'demande.type_vitrine': 'Visitenkarten-Website',
    'demande.type_vitrine_desc': 'Präsentation Ihres Unternehmens',
    'demande.type_ecommerce': 'E-Commerce',
    'demande.type_ecommerce_desc': 'Online-Shop mit Zahlungen',
    'demande.type_portfolio': 'Portfolio',
    'demande.type_portfolio_desc': 'Showcase Ihrer Arbeiten',
    'demande.type_autre': 'Sonstiges',
    'demande.type_autre_desc': 'Anderer Projekttyp',
    'demande.pack_essentiel': 'Essentielles Paket',
    'demande.pack_essentiel_price': '890.- + 19.90.-/Monat',
    'demande.pack_essentiel_desc': 'Professionelle Website ohne Wartung',
    'demande.pack_confort': 'Komfort-Paket',
    'demande.pack_confort_price': '890.- + 39.90.-/Monat',
    'demande.pack_confort_desc': 'Website + monatliche Wartung',
    'demande.pack_croissance': 'Wachstums-Paket',
    'demande.pack_croissance_price': '890.- + 99.90.-/Monat',
    'demande.pack_croissance_desc': 'Website + vollständiges Marketing',
    'demande.mkt_google_ads': 'Google Ads',
    'demande.mkt_google_ads_desc': 'Bezahlte Werbekampagnen auf Google',
    'demande.mkt_seo': 'SEO / Suchmaschinenoptimierung',
    'demande.mkt_seo_desc': 'Optimierung für die 1. Google-Seite',
    'demande.mkt_gmb': 'Google My Business',
    'demande.mkt_gmb_desc': 'Google Maps-Eintrag und Kundenbewertungen',
    'demande.mkt_social': 'Soziale Netzwerke',
    'demande.mkt_social_desc': 'Instagram, Facebook, LinkedIn-Management',
    'demande.mkt_meta': 'Meta / LinkedIn Ads',
    'demande.mkt_meta_desc': 'Anzeigen auf Facebook, Instagram oder LinkedIn',
    'demande.mkt_email': 'E-Mail-Marketing',
    'demande.mkt_email_desc': 'Newsletter und automatisierte E-Mail-Kampagnen',
    'demande.ai_chatbot': 'KI-Chatbot',
    'demande.ai_chatbot_desc': 'Intelligenter virtueller Assistent auf Ihrer Website',
    'demande.ai_auto': 'Aufgabenautomatisierung',
    'demande.ai_auto_desc': 'Automatisierte Workflows (Angebote, E-Mails, CRM)',
    'demande.ai_data': 'KI-Datenanalyse',
    'demande.ai_data_desc': 'Dashboards und intelligente Berichte',
    'demande.ai_content': 'KI-Inhaltsgenerierung',
    'demande.ai_content_desc': 'Texte, Produktbeschreibungen, Blogartikel',
    'demande.palette_bleu': 'Professionelles Blau',
    'demande.palette_bleu_desc': 'Vertrauen, Seriosität, Corporate',
    'demande.palette_vert': 'Naturgrün',
    'demande.palette_vert_desc': 'Ökologie, Gesundheit, Wohlbefinden',
    'demande.palette_rouge': 'Energie-Rot',
    'demande.palette_rouge_desc': 'Dynamismus, Leidenschaft, Dringlichkeit',
    'demande.palette_violet': 'Kreatives Violett',
    'demande.palette_violet_desc': 'Kreativität, Luxus, Innovation',
    'demande.palette_orange': 'Warmes Orange',
    'demande.palette_orange_desc': 'Geselligkeit, Energie, Handwerk',
    'demande.palette_gris': 'Modernes Grau',
    'demande.palette_gris_desc': 'Eleganz, Minimalismus, Tech',
    'demande.palette_rose': 'Elegantes Rosa',
    'demande.palette_rose_desc': 'Schönheit, Mode, Lifestyle',
    'demande.palette_jaune': 'Optimistisches Gelb',
    'demande.palette_jaune_desc': 'Jugend, Kreativität, Sonne',
    'demande.palette_turquoise': 'Frisches Türkis',
    'demande.palette_turquoise_desc': 'Modernität, Frische, Digital',
    'demande.palette_noir': 'Luxus-Schwarz',
    'demande.palette_noir_desc': 'Prestige, High-End, Exklusivität',
    'demande.palette_multi': 'Mehrfarbig / Lebhaft',
    'demande.palette_multi_desc': 'Kreativ, festlich, Vielfalt',
    'demande.palette_none': 'Keine Präferenz',
    'demande.palette_none_desc': 'Lassen Sie unser Team für Sie wählen',
    'confirmation.title': 'Anfrage erfolgreich',
    'confirmation.title_accent': 'gesendet!',
    'confirmation.thanks': 'Danke',
    'confirmation.thanks_sub': ', Ihre Anfrage wurde erhalten.',
    'confirmation.summary': 'Zusammenfassung Ihrer Anfrage',
    'confirmation.next_steps': 'Nächste Schritte',
    'confirmation.back_home': 'Zurück zur Startseite',
    'confirmation.new_request': 'Neue Anfrage',
    'confirmation.full_name': 'Vollständiger Name',
    'confirmation.email': 'E-Mail',
    'confirmation.company': 'Unternehmen',
    'confirmation.pack': 'Gewähltes Paket',
    'confirmation.services': 'Ausgewählte Dienste',
    'confirmation.see_offers': 'Unsere Angebote ansehen',
    'login.title': 'Anmelden',
    'login.subtitle': 'Zugang zu Ihrem Kundenbereich',
    'login.email': 'E-Mail',
    'login.password': 'Passwort',
    'login.forgot': 'Passwort vergessen?',
    'login.submit': 'Anmelden',
    'login.loading': 'Anmelden...',
    'login.no_account': 'Noch kein Konto?',
    'login.signup': 'Konto erstellen',
    'signup.title': 'Konto erstellen',
    'signup.subtitle': 'Treten Sie dem ECR360-Kundenbereich bei',
    'signup.company': 'Unternehmensname',
    'signup.email': 'E-Mail',
    'signup.password': 'Passwort',
    'signup.confirm_password': 'Passwort bestätigen',
    'signup.submit': 'Mein Konto erstellen',
    'signup.loading': 'Erstellen...',
    'signup.already': 'Bereits ein Konto?',
    'signup.login': 'Anmelden',
    'reset.title': 'Passwort vergessen',
    'reset.subtitle': 'Geben Sie Ihre E-Mail ein, um einen Reset-Link zu erhalten.',
    'reset.email': 'E-Mail',
    'reset.submit': 'Link senden',
    'reset.loading': 'Senden...',
    'reset.success': 'E-Mail gesendet! Überprüfen Sie Ihren Posteingang.',
    'reset.back_login': 'Zurück zur Anmeldung',
    'client.dashboard': 'Dashboard',
    'client.subscription': 'Abonnement',
    'client.invoices': 'Rechnungen',
    'client.profile': 'Profil',
    'client.logout': 'Abmelden',
    'client.welcome': 'Willkommen',
  },

  it: {
    'nav.home': 'Home',
    'nav.pricing': 'Offerte & Prezzi',
    'nav.marketing': 'Marketing',
    'nav.ai': 'IA & Automazione',
    'nav.contact': 'Contatto',
    'nav.espace_client': 'Area Cliente',
    'nav.start_project': 'Avvia il mio progetto',
    'home.badge': '🇨🇭 Soluzioni Digitali Svizzere',
    'home.hero_title': 'La tua presenza digitale,',
    'home.hero_title_accent': 'chiavi in mano',
    'home.hero_subtitle': 'Siti web professionali, marketing digitale e automazione IA per PMI svizzere. A partire da',
    'home.hero_subtitle_setup': '890.- setup',
    'home.hero_subtitle_and': 'e',
    'home.hero_subtitle_monthly': '19.90.-/mese',
    'home.cta_offers': 'Vedi le offerte',
    'home.cta_expert': 'Parla con un esperto',
    'home.trust_no_fees': 'Nessun costo nascosto',
    'home.trust_support': 'Supporto in francese',
    'home.trust_cancel': 'Disdetta mensile',
    'home.scroll_discover': 'Scopri',
    'home.stats_sites': 'Siti consegnati',
    'home.stats_clients': 'Clienti soddisfatti',
    'home.stats_delay': 'Tempo di risposta',
    'home.stats_packs': 'Pacchetti disponibili',
    'home.problem_badge': 'Il problema',
    'home.problem_title': 'La tua azienda merita di più di un sito',
    'home.problem_title_strike': 'amatoriale',
    'home.problem_sub': "In Svizzera, il 67% delle PMI perde clienti per mancanza di una presenza digitale professionale. Nel frattempo, i tuoi concorrenti catturano quei clienti online.",
    'home.problem_1': 'Sito non trovabile su Google',
    'home.problem_2': 'Design non adattato per mobile',
    'home.problem_3': 'Nessun sistema di acquisizione lead',
    'home.problem_4': 'Nessun marketing automatizzato',
    'home.solution_badge': 'La nostra soluzione',
    'home.solution_title': 'Una soluzione completa,',
    'home.solution_title_accent': 'senza complessità',
    'home.solution_sub': 'ECR360 Digital gestisce la tua presenza digitale dalla A alla Z. Tu ti concentri sul tuo lavoro, noi gestiamo il resto.',
    'home.solution_1': 'Sito professionale consegnato in 5 giorni',
    'home.solution_2': "SEO Google ottimizzato fin dall'inizio",
    'home.solution_3': 'Automazione marketing integrata',
    'home.solution_4': 'Supporto in francese, team svizzero',
    'home.packs_title': 'I nostri',
    'home.packs_title_accent': 'Pacchetti',
    'home.packs_section_badge': 'I nostri Pacchetti',
    'home.packs_section_title': '3 pacchetti per',
    'home.packs_section_accent': 'ogni ambizione',
    'home.packs_section_sub': 'Scegli il pacchetto adatto alla tua situazione. Aggiorna in qualsiasi momento.',
    'home.packs_compare': 'Vedi confronto dettagliato →',
    'home.pack1_title': 'Sito Senza Manutenzione',
    'home.pack2_title': 'Sito + Manutenzione',
    'home.pack3_title': 'Sito + Marketing',
    'home.pack_setup': 'Setup una tantum',
    'home.pack_month': '/mese',
    'home.pack_choose': 'Scegli',
    'home.pack_popular': 'Il più popolare',
    'home.pack1_f1': 'Sito web professionale',
    'home.pack1_f2': 'Design responsive mobile',
    'home.pack1_f3': 'Hosting incluso',
    'home.pack1_f4': 'Nome di dominio (.ch)',
    'home.pack1_f5': 'Certificato SSL',
    'home.pack1_f6': 'Modulo di contatto',
    'home.pack2_f1': 'Tutto del Pacchetto 1',
    'home.pack2_f2': 'Aggiornamenti mensili',
    'home.pack2_f3': 'Manutenzione tecnica',
    'home.pack2_f4': 'Backup automatici',
    'home.pack2_f5': 'Supporto prioritario',
    'home.pack2_f6': 'Report mensile',
    'home.pack3_f1': 'Tutto del Pacchetto 2',
    'home.pack3_f2': 'Strategia SEO locale',
    'home.pack3_f3': 'Gestione social media',
    'home.pack3_f4': 'Campagne Google Ads',
    'home.pack3_f5': 'Email marketing',
    'home.pack3_f6': 'Dashboard analytics',
    'home.services_badge': 'I nostri Servizi',
    'home.services_title': 'Più che siti web —',
    'home.services_title_accent': 'un ecosistema digitale',
    'home.services_marketing_title': 'Soluzioni Marketing Digitale',
    'home.services_marketing_desc': 'SEO locale, Google Ads, social media, email marketing. Tutto ciò di cui hai bisogno per attrarre e convertire clienti online.',
    'home.services_marketing_discover': 'Scopri →',
    'home.services_ai_title': 'Automazione IA',
    'home.services_ai_desc': "Chatbot, automazione email, qualificazione lead. L'IA al servizio della tua crescita.",
    'home.services_ai_explore': "Esplora l'IA →",
    'home.services_support_title': 'Supporto Svizzero',
    'home.services_support_desc': 'Team in Svizzera, supporto in francese, risposta entro 48h garantita.',
    'home.services_speed_title': 'Consegna in 5 giorni',
    'home.services_speed_desc': 'Il tuo sito professionale online in meno di una settimana.',
    'home.services_analytics_title': 'Analytics incluso',
    'home.services_analytics_desc': 'Dashboard chiaro per monitorare le tue performance in tempo reale.',
    'home.testimonials_title': 'Cosa dicono i',
    'home.testimonials_title_accent': 'nostri clienti',
    'home.testimonials_badge': 'Testimonianze',
    'home.t1_quote': 'ECR360 Digital ha trasformato la nostra presenza online. In 3 mesi, le nostre richieste di preventivo sono aumentate del 60%. Servizio impeccabile, team reattivo.',
    'home.t1_role': 'Responsabile, Dubois Idraulica SA',
    'home.t2_quote': 'Il Pacchetto 3 ci ha permesso di automatizzare il nostro marketing. Ora riceviamo lead qualificati ogni settimana senza alcuno sforzo da parte nostra.',
    'home.t2_role': 'Direttrice, Studio Favre HR',
    'home.t3_quote': 'Rapporto qualità-prezzo imbattibile per la Svizzera. Il nostro sito è bellissimo e il supporto è sempre disponibile. Lo raccomando vivamente.',
    'home.t3_role': 'Fondatore, Müller Consulting',
    'home.cta_section_title': 'Pronto a lanciare il tuo',
    'home.cta_section_accent': 'progetto digitale?',
    'home.cta_section_sub': 'Unisciti a oltre 150 PMI svizzere che si fidano di noi.',
    'home.cta_section_btn': 'Avvia il mio progetto',
    'home.cta_section_btn2': 'Vedi le offerte',
    'home.cta_section_note': 'Consulenza 100% gratuita · Risposta entro 48h · Nessun impegno',
    'pricing.title': 'Le nostre',
    'pricing.title_accent': 'Offerte & Prezzi',
    'pricing.hero_title': 'Scegli il tuo',
    'pricing.hero_title_accent': 'Pacchetto Digitale',
    'pricing.subtitle': 'Soluzioni per ogni budget. Setup una tantum di 890.- poi abbonamento mensile senza impegno.',
    'pricing.setup': 'Setup una tantum',
    'pricing.month': '/mese',
    'pricing.choose': 'Scegli',
    'pricing.popular': 'Il più popolare',
    'pricing.faq_title': 'Domande frequenti',
    'pricing.faq_badge': 'FAQ',
    'pricing.start_btn': 'Avvia il mio progetto',
    'pricing.contact_btn': 'Contatta il nostro team',
    'pricing.custom_need': 'Hai bisogno di una soluzione su misura?',
    'pricing.custom_link': 'Contattaci per un preventivo personalizzato →',
    'pricing.setup_title': 'Informazioni sul setup di CHF 890.-',
    'pricing.setup_desc': 'La tariffa di setup è un investimento unico che copre la creazione completa del tuo sito web: design personalizzato, sviluppo, configurazione tecnica, lancio e formazione iniziale.',
    'pricing.setup_f1': 'Design personalizzato per il tuo brand',
    'pricing.setup_f2': 'Sviluppo & integrazione',
    'pricing.setup_f3': 'Configurazione hosting & dominio',
    "pricing.setup_f4": "Formazione all'utilizzo",
    'pricing.setup_f5': 'Test & ottimizzazione performance',
    'pricing.setup_f6': 'Lancio & verifica',
    'pricing.cta_title': 'Pronto a iniziare?',
    'pricing.cta_sub': 'Consulenza gratuita per scegliere il pacchetto adatto alla tua situazione.',
    'pricing.pack1_title': 'Sito Senza Manutenzione',
    'pricing.pack1_subtitle': 'Perfetto per iniziare',
    'pricing.pack1_ideal': 'Ideale per liberi professionisti e piccole strutture che vogliono una vetrina professionale.',
    'pricing.pack1_cta': 'Scegli Pacchetto 1',
    'pricing.pack1_f1': 'Sito web professionale (fino a 5 pagine)',
    'pricing.pack1_f2': 'Design responsive mobile & tablet',
    'pricing.pack1_f3': 'Hosting svizzero incluso',
    'pricing.pack1_f4': 'Nome di dominio .ch o .com',
    'pricing.pack1_f5': 'Certificato SSL (HTTPS)',
    'pricing.pack1_f6': 'Modulo di contatto',
    'pricing.pack1_f7': 'Integrazione Google Maps',
    'pricing.pack1_f8': 'Aggiornamenti contenuti',
    'pricing.pack1_f9': 'Manutenzione tecnica',
    'pricing.pack1_f10': 'SEO ottimizzato',
    'pricing.pack1_f11': 'Supporto prioritario',
    'pricing.pack1_f12': 'Soluzioni Marketing',
    'pricing.pack2_title': 'Sito + Manutenzione',
    'pricing.pack2_subtitle': 'Il più popolare',
    'pricing.pack2_ideal': 'Ideale per PMI che vogliono una presenza digitale mantenuta e ottimizzata continuamente.',
    'pricing.pack2_cta': 'Scegli Pacchetto 2',
    'pricing.pack2_f1': 'Sito web professionale (fino a 10 pagine)',
    'pricing.pack2_f2': 'Design responsive mobile & tablet',
    'pricing.pack2_f3': 'Hosting svizzero incluso',
    'pricing.pack2_f4': 'Nome di dominio .ch o .com',
    'pricing.pack2_f5': 'Certificato SSL (HTTPS)',
    'pricing.pack2_f6': 'Modulo di contatto avanzato',
    'pricing.pack2_f7': 'Integrazione Google Maps',
    'pricing.pack2_f8': 'Aggiornamenti contenuti (2x/mese)',
    'pricing.pack2_f9': 'Manutenzione tecnica',
    'pricing.pack2_f10': 'SEO di base',
    'pricing.pack2_f11': 'Supporto prioritario (48h)',
    'pricing.pack2_f12': 'Soluzioni Marketing',
    'pricing.pack3_title': 'Sito + Marketing',
    'pricing.pack3_subtitle': 'Soluzione completa',
    'pricing.pack3_ideal': 'Ideale per aziende ambiziose che vogliono generare lead e crescere rapidamente.',
    'pricing.pack3_cta': 'Scegli Pacchetto 3',
    'pricing.pack3_f1': 'Sito web professionale (pagine illimitate)',
    'pricing.pack3_f2': 'Design responsive mobile & tablet',
    'pricing.pack3_f3': 'Hosting svizzero incluso',
    'pricing.pack3_f4': 'Nome di dominio .ch o .com',
    'pricing.pack3_f5': 'Certificato SSL (HTTPS)',
    'pricing.pack3_f6': 'Moduli avanzati + CRM',
    'pricing.pack3_f7': 'Integrazione Google Maps',
    'pricing.pack3_f8': 'Aggiornamenti illimitati',
    'pricing.pack3_f9': 'Manutenzione tecnica completa',
    'pricing.pack3_f10': 'SEO avanzato + report mensile',
    'pricing.pack3_f11': 'Supporto dedicato (24h)',
    'pricing.pack3_f12': 'Google Ads + Social Media + Email',
    'pricing.faq1_q': "Posso cambiare pacchetto dopo l'iscrizione?",
    'pricing.faq1_a': 'Sì, puoi fare upgrade o downgrade del tuo pacchetto in qualsiasi momento. La modifica ha effetto al prossimo ciclo di fatturazione.',
    'pricing.faq2_q': 'Il setup di 890.- è obbligatorio?',
    'pricing.faq2_a': 'Sì, il setup una tantum di CHF 890.- copre la creazione, il design e il lancio del tuo sito. Viene pagato una sola volta.',
    'pricing.faq3_q': 'Posso disdire in qualsiasi momento?',
    'pricing.faq3_a': 'Sì, i nostri abbonamenti non hanno impegno a lungo termine. Puoi disdire con un preavviso di un mese.',
    'pricing.faq4_q': 'Cosa succede al mio sito se disdico?',
    'pricing.faq4_a': 'Mantieni il tuo nome di dominio e ti forniamo un export del tuo sito. Rimani proprietario dei tuoi contenuti.',
    'pricing.faq5_q': 'I prezzi sono in CHF?',
    'pricing.faq5_a': 'Sì, tutti i nostri prezzi sono in Franchi Svizzeri (CHF), IVA inclusa.',
    'pricing.faq6_q': 'Offrite tariffe per le associazioni?',
    'pricing.faq6_a': 'Sì, offriamo tariffe preferenziali per le associazioni e le organizzazioni senza scopo di lucro. Contattateci per un preventivo personalizzato.',
    'marketing.badge': 'Soluzioni Marketing Digitale',
    'marketing.title': 'Aumenta la tua',
    'marketing.title_accent': 'visibilità online',
    'marketing.subtitle': 'Strategie di marketing digitale complete per attirare più clienti e far crescere la tua attività.',
    'marketing.cta': 'Avvia il mio progetto marketing',
    'marketing.cta_audit': 'Audit gratuito della mia presenza',
    'marketing.partner_title': 'Partner certificato Google',
    'marketing.partner_sub': "Padroneggiamo l'intero ecosistema Google per la tua crescita",
    'marketing.services_badge': 'I nostri servizi',
    'marketing.services_title': 'Tutto ciò di cui hai bisogno',
    'marketing.services_sub': 'Dalla visibilità locale alla pubblicità a pagamento, social media ed email marketing.',
    'marketing.process_badge': 'Il nostro processo',
    'marketing.process_title': 'Come funziona',
    'marketing.process_1_title': 'Audit Digitale',
    'marketing.process_1_desc': 'Analisi completa della tua presenza digitale attuale e di quella dei tuoi concorrenti.',
    'marketing.process_2_title': 'Strategia Su Misura',
    'marketing.process_2_desc': 'Definizione di una strategia marketing adattata al tuo settore, obiettivi e budget.',
    'marketing.process_3_title': 'Implementazione',
    'marketing.process_3_desc': 'Dispiegamento delle azioni marketing con monitoraggio settimanale e ottimizzazioni continue.',
    'marketing.process_4_title': 'Misurazione & Ottimizzazione',
    'marketing.process_4_desc': 'Reporting mensile con KPI chiave e aggiustamenti strategici per massimizzare il ROI.',
    'marketing.results_badge': 'Risultati concreti',
    'marketing.results_title': 'Numeri che parlano da soli',
    'marketing.results_sub': 'I nostri clienti PMI svizzeri vedono risultati misurabili fin dai primi mesi. Performance medie su 6 mesi.',
    'marketing.cta_section_title': 'Pronto a potenziare il tuo marketing?',
    'marketing.cta_section_sub': 'Audit digitale gratuito della tua presenza attuale. Senza impegno.',
    'marketing.cta_audit_free': 'Richiedi il mio audit gratuito',
    'marketing.cta_submit': 'Invia il mio progetto',
    'ai.badge': 'IA & Automazione',
    'ai.title': 'Automatizza la tua',
    'ai.title_accent': 'crescita',
    'ai.subtitle': "L'intelligenza artificiale al servizio della tua azienda. Risparmia tempo, riduci i costi e aumenta le conversioni.",
    'ai.cta': 'Scopri le nostre soluzioni IA',
    'ai.cta_demo': 'Richiedi una demo IA',
    'ai.cta_pricing': 'Vedi i prezzi',
    'ai.chatbot_label': 'Chatbot ECR360 — In diretta sul tuo sito',
    'ai.chatbot_active': 'Attivo 24/7',
    'ai.solutions_badge': 'Soluzioni IA',
    'ai.solutions_title': "Tutto ciò che l'IA può fare per te",
    'ai.usecases_badge': "Casi d'uso",
    'ai.usecases_title': "L'IA nel tuo settore",
    'ai.roi_badge': 'ROI Stimato',
    'ai.roi_title': "Quanto può farti risparmiare l'IA?",
    'ai.roi_sub': 'In media, i nostri clienti PMI risparmiano',
    'ai.roi_highlight': '23h a settimana',
    'ai.roi_sub2': "grazie all'automazione IA.",
    'ai.roi_stat1_label': 'Risparmiate/settimana',
    'ai.roi_stat1_sub': 'In attività ripetitive',
    'ai.roi_stat2_label': 'Tasso di risoluzione',
    'ai.roi_stat2_sub': 'Chatbot IA',
    'ai.roi_stat3_label': 'Più conversioni',
    'ai.roi_stat3_sub': 'Su lead automatizzati',
    'ai.cta_section_title': 'Pronto ad automatizzare il tuo business?',
    'ai.cta_section_sub': "Demo gratuita di 30 minuti per vedere l'IA in azione nel tuo settore.",
    'ai.cta_demo_free': 'Richiedi la mia demo gratuita',
    'ai.sol1_title': 'Chatbot Intelligente',
    'ai.sol1_desc': 'Rispondi automaticamente alle domande frequenti dei tuoi visitatori 24/7. Qualifica i lead prima che ti contattino.',
    'ai.sol1_benefit': 'Risparmia 15h/settimana di supporto clienti',
    'ai.sol1_tag': 'IA Conversazionale',
    'ai.sol2_title': 'Email Automation',
    'ai.sol2_desc': 'Sequenze di email automatiche attivate dalle azioni dei tuoi prospect. Nurturing intelligente fino alla conversione.',
    'ai.sol2_benefit': '4x più conversioni sui lead freddi',
    'ai.sol2_tag': 'Marketing Automation',
    'ai.sol3_title': 'Lead Scoring IA',
    'ai.sol3_desc': "L'IA analizza il comportamento dei tuoi visitatori e valuta ogni lead in base alla probabilità di conversione.",
    'ai.sol3_benefit': '60% di riduzione del tempo di qualificazione',
    'ai.sol3_tag': 'Intelligenza Artificiale',
    'ai.sol4_title': 'Reporting Automatizzato',
    'ai.sol4_desc': 'Report di performance generati automaticamente ogni settimana. Insight azionabili senza alcuno sforzo.',
    'ai.sol4_benefit': 'Decisioni 3x più veloci basate sui dati',
    'ai.sol4_tag': 'Business Intelligence',
    'ai.sol5_title': 'Integrazioni & CRM',
    'ai.sol5_desc': 'Connetti il tuo sito al tuo CRM, agenda e strumenti aziendali. Tutto sincronizzato automaticamente.',
    'ai.sol5_benefit': '8h/settimana risparmiate su attività amministrative',
    'ai.sol5_tag': 'Automazione',
    'ai.sol6_title': 'Generazione di Contenuti IA',
    'ai.sol6_desc': "L'IA genera varianti di testi per le tue pubblicità, email e post social. Testa più velocemente, ottimizza continuamente.",
    'ai.sol6_benefit': '10x più variazioni testate al mese',
    'ai.sol6_tag': 'IA Generativa',
    'ai.uc1_sector': 'Artigiani & PMI',
    'ai.uc1_scenario': "Un idraulico riceve automaticamente le richieste di preventivo via chatbot, qualificate dall'IA, con notifica SMS istantanea.",
    'ai.uc1_saving': '12h/settimana risparmiate',
    'ai.uc2_sector': 'Liberi professionisti',
    'ai.uc2_scenario': 'Uno studio medico automatizza i promemoria degli appuntamenti, i follow-up dei pazienti e le newsletter mensili.',
    'ai.uc2_saving': '8h/settimana risparmiate',
    'ai.uc3_sector': 'Commercio & Retail',
    'ai.uc3_scenario': "Un negozio invia email di follow-up automatiche dopo l'abbandono del carrello, con offerte personalizzate dall'IA.",
    'ai.uc3_saving': '+35% di recupero carrelli',
    'contact.badge': 'Contattaci',
    'contact.title': 'Parliamo del tuo',
    'contact.title_accent': 'progetto',
    'contact.subtitle': 'Il nostro team è disponibile per rispondere a tutte le tue domande e accompagnarti nella tua trasformazione digitale.',
    'contact.firstname': 'Nome',
    'contact.lastname': 'Cognome',
    'contact.email': 'Email',
    'contact.company': 'Azienda',
    'contact.phone': 'Telefono',
    'contact.pack': 'Pacchetto desiderato',
    'contact.message': 'Messaggio',
    'contact.send': 'Invia messaggio',
    'contact.sending': 'Invio in corso...',
    'contact.success_title': 'Messaggio inviato!',
    'contact.success_sub': 'Grazie per il tuo messaggio. Il nostro team risponderà entro 48 ore.',
    'contact.back_home': 'Torna alla home',
    'contact.form_title': 'Inviaci la tua richiesta',
    'contact.form_sub': 'Tutti i campi contrassegnati con * sono obbligatori.',
    'contact.message_placeholder': 'Parlaci della tua azienda, dei tuoi obiettivi digitali e di cosa stai cercando...',
    'contact.privacy': 'Inviando questo modulo, accetti che i tuoi dati vengano utilizzati per contattarti riguardo al tuo progetto. In conformità con la LPD svizzera, puoi richiedere la cancellazione dei tuoi dati in qualsiasi momento.',
    'contact.coords_title': 'I nostri contatti',
    'contact.next_title': 'Cosa succede dopo',
    'contact.next_1': 'Analizziamo la tua richiesta e il tuo settore',
    'contact.next_2': 'Chiamata di 30 min per capire i tuoi obiettivi',
    'contact.next_3': 'Proposta personalizzata con preventivo trasparente',
    'contact.next_4': 'Avvio del progetto entro 5 giorni lavorativi',
    'contact.why_title': 'Perché ECR360 Digital?',
    'contact.why_1': '150+ siti consegnati in Svizzera',
    'contact.why_2': 'Team 100% svizzero, supporto in francese',
    'contact.why_3': 'Prezzi trasparenti, nessuna sorpresa',
    'contact.why_4': 'Disdetta mensile senza penale',
    'contact.why_5': 'Consulenza iniziale gratuita',
    'contact.packs_title': 'Promemoria delle nostre offerte',
    'contact.packs_link': 'Vedi confronto completo dei pacchetti →',
    'contact.pack_opt_default': 'Pacchetto desiderato',
    'contact.pack_opt_1': 'Pacchetto 1 — Sito Senza Manutenzione (19.90.-/mese)',
    'contact.pack_opt_2': 'Pacchetto 2 — Sito + Manutenzione (39.90.-/mese)',
    'contact.pack_opt_3': 'Pacchetto 3 — Sito + Marketing (99.90.-/mese)',
    'contact.pack_opt_marketing': 'Solo Marketing Digitale',
    'contact.pack_opt_ai': 'Solo Automazione IA',
    'contact.pack_opt_custom': 'Soluzione su misura',
    'contact.info_email': 'Email',
    'contact.info_website': 'Sito web',
    'contact.info_location': 'Posizione',
    'contact.info_response': 'Risposta',
    'contact.info_response_val': 'Entro 48 ore',
    'contact.pack_reminder_chf': 'CHF/mese',
    'demande.badge': 'Richiesta di Progetto',
    'demande.title': 'Avvia il tuo',
    'demande.title_accent': 'progetto digitale',
    'demande.subtitle': 'Compila questo modulo e il nostro team ti contatterà entro 48h per discutere del tuo progetto.',
    'demande.step1': 'Le tue informazioni',
    'demande.step2': 'Tipo di sito',
    'demande.step3': 'Pacchetto scelto',
    'demande.step4': 'Servizi aggiuntivi',
    'demande.step4_sub': 'Oltre al tuo sito web, hai bisogno di altri servizi? Seleziona tutto ciò che ti interessa.',
    'demande.step5': 'Descrizione del progetto',
    'demande.step6': 'Logo',
    'demande.step7': 'Palette di colori',
    'demande.step7_sub': "Scegli una palette di colori che corrisponda all'immagine che vuoi dare al tuo sito.",
    'demande.step8': 'Informazioni aggiuntive',
    'demande.optional': '(opzionale)',
    'demande.required_fields': '* Campi obbligatori. I tuoi dati sono trattati in modo confidenziale.',
    'demande.submit': 'Invia la mia richiesta',
    'demande.submitting': 'Invio in corso...',
    'demande.firstname': 'Nome',
    'demande.lastname': 'Cognome',
    'demande.email': 'Email',
    'demande.phone': 'Telefono',
    "demande.company": "Nome dell'azienda",
    'demande.url': 'URL desiderato',
    'demande.description_placeholder': 'Descrivi il tuo progetto, i tuoi obiettivi, il tuo settore...',
    'demande.deadline': 'Scadenza desiderata',
    'demande.extra_message': 'Messaggio aggiuntivo',
    'demande.extra_message_placeholder': 'Qualsiasi informazione aggiuntiva che desideri comunicarci...',
    'demande.logo_drop': 'Trascina il tuo logo qui',
    'demande.logo_formats': 'PNG, JPG o SVG — max 5MB',
    'demande.logo_delete': 'Elimina',
    'demande.logo_create': 'Non ho un logo — vorrei farne creare uno',
    'demande.logo_create_sub': 'Il nostro team ti proporrà opzioni di creazione del logo adatte alla tua identità.',
    'demande.palette_selected': 'Palette selezionata:',
    'demande.palette_clear': 'Cancella',
    'demande.marketing_title': 'Soluzioni Marketing Digitale',
    'demande.marketing_sub': 'Google, social network, pubblicità online',
    'demande.ai_title': 'Automazione & Intelligenza Artificiale',
    'demande.ai_sub': 'Chatbot, workflow automatizzati, IA su misura',
    'demande.email_pro_title': 'Indirizzi email professionali',
    'demande.email_pro_sub': 'Es.: contatto@vostra-azienda.ch — Email professionali ospitate in Svizzera con il vostro nome di dominio.',
    'demande.success_title': 'Richiesta inviata!',
    'demande.success_sub': 'La tua richiesta di progetto è stata ricevuta. Il nostro team ti contatterà entro',
    'demande.success_sub2': '48 ore',
    'demande.success_sub3': 'per discutere del tuo progetto.',
    'demande.pack_label': 'Pacchetto scelto',
    'demande.site_type_label': 'Tipo di sito',
    'demande.marketing_count': 'servizio/i',
    'demande.ai_count': 'servizio/i',
    'demande.back_home': 'Torna alla home',
    'demande.see_offers': 'Vedi le nostre offerte',
    'demande.type_vitrine': 'Sito Vetrina',
    'demande.type_vitrine_desc': 'Presentazione della tua azienda',
    'demande.type_ecommerce': 'E-Commerce',
    'demande.type_ecommerce_desc': 'Negozio online con pagamenti',
    'demande.type_portfolio': 'Portfolio',
    'demande.type_portfolio_desc': 'Showcase delle tue realizzazioni',
    'demande.type_autre': 'Altro',
    'demande.type_autre_desc': 'Altro tipo di progetto',
    'demande.pack_essentiel': 'Pacchetto Essenziale',
    'demande.pack_essentiel_price': '890.- + 19.90.-/mese',
    'demande.pack_essentiel_desc': 'Sito professionale senza manutenzione',
    'demande.pack_confort': 'Pacchetto Comfort',
    'demande.pack_confort_price': '890.- + 39.90.-/mese',
    'demande.pack_confort_desc': 'Sito + manutenzione mensile',
    'demande.pack_croissance': 'Pacchetto Crescita',
    'demande.pack_croissance_price': '890.- + 99.90.-/mese',
    'demande.pack_croissance_desc': 'Sito + marketing completo',
    'demande.mkt_google_ads': 'Google Ads',
    'demande.mkt_google_ads_desc': 'Campagne pubblicitarie a pagamento su Google',
    'demande.mkt_seo': 'SEO / Referenziamento',
    'demande.mkt_seo_desc': "Ottimizzazione per apparire in 1a pagina Google",
    'demande.mkt_gmb': 'Google My Business',
    'demande.mkt_gmb_desc': 'Scheda Google Maps e recensioni clienti',
    'demande.mkt_social': 'Social Media',
    'demande.mkt_social_desc': 'Gestione Instagram, Facebook, LinkedIn',
    'demande.mkt_meta': 'Meta / LinkedIn Ads',
    'demande.mkt_meta_desc': 'Pubblicità su Facebook, Instagram o LinkedIn',
    'demande.mkt_email': 'Email Marketing',
    'demande.mkt_email_desc': 'Newsletter e campagne email automatizzate',
    'demande.ai_chatbot': 'Chatbot IA',
    'demande.ai_chatbot_desc': 'Assistente virtuale intelligente sul tuo sito',
    'demande.ai_auto': 'Automazione delle attività',
    'demande.ai_auto_desc': 'Workflow automatici (preventivi, email, CRM)',
    'demande.ai_data': 'Analisi dati IA',
    'demande.ai_data_desc': 'Dashboard e report intelligenti',
    'demande.ai_content': 'Generazione contenuti IA',
    'demande.ai_content_desc': 'Testi, descrizioni prodotti, articoli blog',
    'demande.palette_bleu': 'Blu Professionale',
    'demande.palette_bleu_desc': 'Fiducia, serietà, corporate',
    'demande.palette_vert': 'Verde Natura',
    'demande.palette_vert_desc': 'Ecologia, salute, benessere',
    'demande.palette_rouge': 'Rosso Energia',
    'demande.palette_rouge_desc': 'Dinamismo, passione, urgenza',
    'demande.palette_violet': 'Viola Creativo',
    'demande.palette_violet_desc': 'Creatività, lusso, innovazione',
    'demande.palette_orange': 'Arancione Caldo',
    'demande.palette_orange_desc': 'Convivialità, energia, artigianato',
    'demande.palette_gris': 'Grigio Moderno',
    'demande.palette_gris_desc': 'Eleganza, minimalismo, tech',
    'demande.palette_rose': 'Rosa Elegante',
    'demande.palette_rose_desc': 'Bellezza, moda, lifestyle',
    'demande.palette_jaune': 'Giallo Ottimista',
    'demande.palette_jaune_desc': 'Giovinezza, creatività, sole',
    'demande.palette_turquoise': 'Turchese Fresco',
    'demande.palette_turquoise_desc': 'Modernità, freschezza, digital',
    'demande.palette_noir': 'Nero Lusso',
    'demande.palette_noir_desc': 'Prestigio, alto di gamma, esclusività',
    'demande.palette_multi': 'Multicolore / Vivace',
    'demande.palette_multi_desc': 'Creativo, festivo, diversità',
    'demande.palette_none': 'Nessuna preferenza',
    'demande.palette_none_desc': 'Lascia che il nostro team scelga per te',
    'confirmation.title': 'Richiesta inviata',
    'confirmation.title_accent': 'con successo!',
    'confirmation.thanks': 'Grazie',
    'confirmation.thanks_sub': ', la tua richiesta è stata ricevuta.',
    'confirmation.summary': 'Riepilogo della tua richiesta',
    'confirmation.next_steps': 'Prossimi passi',
    'confirmation.back_home': 'Torna alla home',
    'confirmation.new_request': 'Nuova richiesta',
    'confirmation.full_name': 'Nome completo',
    'confirmation.email': 'Email',
    'confirmation.company': 'Azienda',
    'confirmation.pack': 'Pacchetto scelto',
    'confirmation.services': 'Servizi selezionati',
    'confirmation.see_offers': 'Vedi le nostre offerte',
    'login.title': 'Accedi',
    'login.subtitle': 'Accedi alla tua area cliente',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.forgot': 'Password dimenticata?',
    'login.submit': 'Accedi',
    'login.loading': 'Accesso...',
    'login.no_account': 'Non hai ancora un account?',
    'login.signup': 'Crea un account',
    'signup.title': 'Crea un account',
    'signup.subtitle': "Unisciti all'area cliente ECR360",
    "signup.company": "Nome dell'azienda",
    'signup.email': 'Email',
    'signup.password': 'Password',
    'signup.confirm_password': 'Conferma password',
    'signup.submit': 'Crea il mio account',
    'signup.loading': 'Creazione...',
    'signup.already': 'Hai già un account?',
    'signup.login': 'Accedi',
    'reset.title': 'Password dimenticata',
    'reset.subtitle': 'Inserisci la tua email per ricevere un link di reset.',
    'reset.email': 'Email',
    'reset.submit': 'Invia link',
    'reset.loading': 'Invio...',
    'reset.success': 'Email inviata! Controlla la tua casella di posta.',
    'reset.back_login': 'Torna al login',
    'client.dashboard': 'Dashboard',
    'client.subscription': 'Abbonamento',
    'client.invoices': 'Fatture',
    'client.profile': 'Profilo',
    'client.logout': 'Disconnetti',
    'client.welcome': 'Benvenuto',
  },
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>('fr');

  useEffect(() => {
    const stored = localStorage.getItem('ecr360_lang') as Language | null;
    if (stored && ['fr', 'en', 'de', 'it'].includes(stored)) {
      setLangState(stored);
    }
  }, []);

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    localStorage.setItem('ecr360_lang', newLang);
  };

  const t = (key: string): string => {
    return translations[lang]?.[key] ?? translations['fr']?.[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}
